import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { adminApi } from "./api";
import { useAdminAuth } from "./auth";
import { AuthLayout } from "./workspace";
import { Badge, Button, ButtonLink, EmptyState, LoadingBlock, PageTitle } from "./ui";
import { ADMIN_BASE_PATH } from "./admin-path";

export function AdminLoginPage() { const auth=useAdminAuth();const navigate=useNavigate();const [username,setUsername]=useState("admin");const [password,setPassword]=useState("");const [error,setError]=useState("");const [busy,setBusy]=useState(false);const submit=async(e:FormEvent)=>{e.preventDefault();setBusy(true);setError("");try{await auth.login(username,password);navigate(ADMIN_BASE_PATH);}catch(err){setError(err instanceof Error?err.message:"登录失败");}finally{setBusy(false)}};return <AuthLayout title="管理员登录" description="独立权限域，仅用于 PPTDeep 运营、支持和内部实验。"><form className="auth-form" onSubmit={submit}><label>管理员账号<input value={username} onChange={(e)=>setUsername(e.target.value)} autoComplete="username"/></label><label>密码<input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} autoComplete="current-password" placeholder="请输入管理员密码"/></label>{error&&<div className="form-error">{error}</div>}<Button type="submit" disabled={busy}>{busy?"验证中…":"进入管理后台"}</Button><p className="security-note">管理员会话使用独立 HttpOnly Cookie，并记录操作审计。</p></form></AuthLayout>; }

type AdminStorageOverview = {
  machine: { totalBytes:number; usedBytes:number; freeBytes:number; usagePercent:number; measuredAt:string };
  tracked: { userBytes:number; physicalUserBytes:number; latestMeasuredAt?:string };
  topUsers: Array<{ id:string; nickname:string; usedBytes:number; permanentBytes:number; measuredAt?:string }>;
  topProjects: Array<{ id:string; title:string; userId:string; usedBytes:number; permanentBytes:number; measuredAt?:string }>;
};

export function AdminOverviewPage(){
  const [summary,setSummary]=useState<Record<string,number>>();
  const [storage,setStorage]=useState<AdminStorageOverview>();
  const [storageError,setStorageError]=useState("");
  const [showStorageDetails,setShowStorageDetails]=useState(false);
  useEffect(()=>{
    adminApi<{summary:Record<string,number>}>("/api/admin/summary").then((data)=>setSummary(data.summary)).catch(()=>setSummary({}));
    adminApi<{storage:AdminStorageOverview}>("/api/admin/storage-overview")
      .then((data)=>setStorage(data.storage))
      .catch((error)=>setStorageError(error instanceof Error?error.message:"磁盘空间读取失败"));
  },[]);
  const usagePercent=Math.max(0,Math.min(100,storage?.machine.usagePercent||0));
  return <>
    <PageTitle eyebrow="运营概览" title="PPTDeep 管理后台" description="关注项目流转、生成稳定性与内部实验状态。"/>
    <div className="metric-strip admin-metrics">{[["用户",summary?.users],["项目",summary?.projects],["生成批次",summary?.generations],["渲染结果",summary?.renders],["排队任务",summary?.queuedTasks]].map(([label,value])=><article key={String(label)}><span>{label}</span><strong>{value??"–"}</strong></article>)}</div>
    <section className="admin-machine-storage" aria-label="当前机器磁盘空间">
      <div className="admin-machine-storage-head">
        <div><span>机器磁盘</span><h2>当前服务器空间</h2><p>{storage?`采样于 ${formatDate(storage.machine.measuredAt)}`:"正在读取文件系统容量…"}</p></div>
        <Button type="button" variant="secondary" disabled={!storage} aria-expanded={showStorageDetails} onClick={()=>setShowStorageDetails((value)=>!value)}>{showStorageDetails?"收起详情":"查看详情"}</Button>
      </div>
      {storage?<>
        <div className="admin-machine-storage-metrics">
          <div className="primary"><span>剩余空间</span><strong>{formatStorageBytes(storage.machine.freeBytes)}</strong><small>占总容量 {(100-usagePercent).toFixed(1)}%</small></div>
          <div><span>已使用</span><strong>{formatStorageBytes(storage.machine.usedBytes)}</strong></div>
          <div><span>总容量</span><strong>{formatStorageBytes(storage.machine.totalBytes)}</strong></div>
          <div><span>用户目录实际占用</span><strong>{formatStorageBytes(storage.tracked.physicalUserBytes)}</strong><small>其中永久文件 {formatStorageBytes(storage.tracked.userBytes)}</small></div>
        </div>
        <div className="admin-machine-storage-progress" role="progressbar" aria-label="机器磁盘使用率" aria-valuemin={0} aria-valuemax={100} aria-valuenow={usagePercent}><i style={{width:`${usagePercent}%`}}/></div>
        <p className="admin-machine-storage-caption">机器已使用 {usagePercent.toFixed(1)}%，剩余 {formatStorageBytes(storage.machine.freeBytes)}。用户永久文件统计最近更新于 {storage.tracked.latestMeasuredAt?formatDate(storage.tracked.latestMeasuredAt):"等待首次统计"}。</p>
        {showStorageDetails&&<div className="admin-storage-rankings">
          <StorageRanking title="Top 10 用户" description="按用户目录实际占用排序，并同时标出永久额度占用。" items={storage.topUsers.map((item)=>({id:item.id,label:item.nickname,meta:item.id,bytes:item.usedBytes,permanentBytes:item.permanentBytes}))}/>
          <StorageRanking title="Top 10 项目" description="包含素材、审计、成品和渐进预览，并同时标出永久额度占用。" items={storage.topProjects.map((item)=>({id:item.id,label:item.title,meta:`${item.id} · 用户 ${item.userId}`,bytes:item.usedBytes,permanentBytes:item.permanentBytes}))}/>
          <p className="admin-storage-more-note">当前仅展示前 10 条；查看更多与分页后续开放。</p>
        </div>}
      </>:storageError?<div className="form-error" role="alert">{storageError}</div>:<LoadingBlock/>}
    </section>
    <div className="admin-dashboard-grid"><section className="content-section"><div className="section-row"><div><h2>需要关注</h2><p>失败聚合与告警将在阶段 J 接入。</p></div></div><EmptyState title="当前没有运营告警" description="模型、Worker 和 renderer 的系统性异常会显示在这里。"/></section><section className="content-section"><h2>环境状态</h2><dl className="health-list"><div><dt>数据库</dt><dd><Badge tone="success">MySQL</Badge></dd></div><div><dt>Feature Policy</dt><dd><Badge tone="brand">已启用</Badge></dd></div><div><dt>支付</dt><dd><Badge>模拟 · 未接入</Badge></dd></div></dl></section></div>
  </>;
}

function StorageRanking({title,description,items}:{title:string;description:string;items:Array<{id:string;label:string;meta:string;bytes:number;permanentBytes:number}>}){
  const max=Math.max(1,...items.map((item)=>item.bytes));
  return <section><header><div><h3>{title}</h3><p>{description}</p></div><span>{items.length} 条</span></header>{items.length?<ol>{items.map((item,index)=><li key={item.id}><b>{index+1}</b><div><strong title={item.label}>{item.label}</strong><small title={item.meta}>{item.meta}</small><i><span style={{width:`${Math.max(2,item.bytes/max*100)}%`}}/></i></div><em>{formatStorageBytes(item.bytes)}<small>永久 {formatStorageBytes(item.permanentBytes)}</small></em></li>)}</ol>:<p className="admin-storage-empty">暂无已统计数据</p>}</section>;
}

function formatStorageBytes(bytes:number){
  const value=Math.max(0,Number(bytes)||0);
  if(value<1024)return `${value.toFixed(0)} B`;
  if(value<1024**2)return `${(value/1024).toFixed(1)} KiB`;
  if(value<1024**3)return `${(value/1024**2).toFixed(1)} MiB`;
  if(value<1024**4)return `${(value/1024**3).toFixed(2)} GiB`;
  return `${(value/1024**4).toFixed(2)} TiB`;
}

type AdminTaskListItem = {
  numericId: number;
  id: string;
  userId: number;
  userPublicId: string;
  phone?: string;
  userNickname?: string;
  title: string;
  status: string;
  progressPercent: number;
  progressText?: string;
  retryCount: number;
  failureStage?: string;
  errorCode?: string;
  errorMessage?: string;
  generationMode: string;
  currentGenerationId?: string;
  currentGenerationStatus?: string;
  currentRenderId?: string;
  currentRenderer?: string;
  currentRenderStatus?: string;
  latestWorkerTaskId?: string;
  latestWorkerTaskStatus?: string;
  updatedAt: string;
};

type TaskPagination = { page: number; pageSize: number; total: number; totalPages: number };

export function AdminProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activePhone = searchParams.get("phone") || "";
  const activeTaskId = searchParams.get("taskId") || "";
  const requestedPage = Math.max(1, Number(searchParams.get("page")) || 1);
  const [phone, setPhone] = useState(activePhone);
  const [taskId, setTaskId] = useState(activeTaskId);
  const [items, setItems] = useState<AdminTaskListItem[]>();
  const [pagination, setPagination] = useState<TaskPagination>();
  const [error, setError] = useState("");

  const loadTasks = useCallback(async () => {
    setItems(undefined);
    setError("");
    const query = new URLSearchParams({ page: String(requestedPage), pageSize: "20" });
    if (activePhone) query.set("phone", activePhone);
    if (activeTaskId) query.set("taskId", activeTaskId);
    try {
      const data = await adminApi<{ tasks: AdminTaskListItem[]; pagination: TaskPagination }>(`/api/admin/tasks?${query}`);
      setItems(data.tasks || []);
      setPagination(data.pagination);
    } catch (caught) {
      setItems([]);
      setPagination(undefined);
      setError(caught instanceof Error ? caught.message : "任务列表加载失败");
    }
  }, [activePhone, activeTaskId, requestedPage]);

  useEffect(() => { void loadTasks(); }, [loadTasks]);
  useEffect(() => { setPhone(activePhone); setTaskId(activeTaskId); }, [activePhone, activeTaskId]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams();
    const normalizedPhone = phone.replace(/[\s-]/g, "");
    if (normalizedPhone) next.set("phone", normalizedPhone);
    if (taskId.trim()) next.set("taskId", taskId.trim());
    next.set("page", "1");
    setSearchParams(next);
  };
  const reset = () => {
    setPhone("");
    setTaskId("");
    setSearchParams(new URLSearchParams({ page: "1" }));
  };
  const goToPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(page));
    setSearchParams(next);
  };

  return <>
    <PageTitle
      eyebrow="任务运维"
      title="任务列表"
      description="默认显示最近更新的 20 条任务；按手机号或任务 ID 定位后，可进入详情查看规划、渲染、Worker 与失败信息。"
      action={<Button type="button" variant="secondary" onClick={() => void loadTasks()}>刷新列表</Button>}
    />
    <form className="admin-task-search" onSubmit={search}>
      <label><span>手机号</span><input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" maxLength={13} placeholder="支持 3–11 位手机号片段" /></label>
      <label><span>任务 ID</span><input value={taskId} onChange={(event) => setTaskId(event.target.value)} maxLength={160} placeholder="公开 ID 或数据库数字 ID" /></label>
      <Button type="submit">查询</Button>
      <Button type="button" variant="ghost" onClick={reset}>重置</Button>
    </form>
    {error && <div className="form-error admin-task-message" role="alert">{error}</div>}
    <div className="admin-task-summary">
      <span>共 <strong>{pagination?.total ?? "–"}</strong> 条</span>
      <span>第 {pagination?.page ?? requestedPage} / {pagination?.totalPages ?? "–"} 页</span>
    </div>
    {!items ? <LoadingBlock/> : items.length ? <div className="data-table admin-task-table">
      <div className="data-row data-head"><span>任务 / 用户</span><span>模式</span><span>任务状态</span><span>Generation / Render</span><span>Worker</span><span>失败诊断</span><span>更新时间</span><span>操作</span></div>
      {items.map((item) => <div className="data-row" key={item.id}>
        <strong className="admin-task-identity">{item.title || "未命名任务"}<small>{item.id} · #{item.numericId}<br/>{item.phone || "未绑定手机号"} · {item.userNickname || item.userPublicId}</small></strong>
        <span><Badge tone="brand">{taskModeLabel(item.generationMode)}</Badge></span>
        <span><Badge tone={taskStatusTone(item.status)}>{taskStatusLabel(item.status)}</Badge><small className="admin-task-progress">{item.progressPercent ?? 0}% · {item.progressText || "无进度说明"}</small></span>
        <span className="admin-task-pipeline"><b>{item.currentGenerationStatus ? `G: ${taskStatusLabel(item.currentGenerationStatus)}` : "G: –"}</b><small>{item.currentRenderer ? `${item.currentRenderer} · R: ${taskStatusLabel(item.currentRenderStatus || "")}` : "R: –"}</small></span>
        <span>{item.latestWorkerTaskStatus ? <Badge tone={taskStatusTone(item.latestWorkerTaskStatus)}>{taskStatusLabel(item.latestWorkerTaskStatus)}</Badge> : "–"}</span>
        <span className={item.errorMessage ? "admin-task-error" : "admin-task-muted"}>{item.errorCode || item.failureStage || "–"}<small>{item.errorMessage || "无失败信息"}</small></span>
        <span>{formatDate(item.updatedAt)}</span>
        <span><ButtonLink variant="secondary" to={`${ADMIN_BASE_PATH}/projects/${encodeURIComponent(item.id)}`}>查看详情</ButtonLink></span>
      </div>)}
    </div> : <EmptyState
      title="没有匹配的任务"
      description={activePhone || activeTaskId ? "请检查手机号或任务 ID，也可以重置筛选查看最近任务。" : "用户创建任务后会显示在这里。"}
    />}
    {pagination && pagination.totalPages > 1 && <nav className="admin-task-pagination" aria-label="任务列表分页">
      <Button type="button" variant="secondary" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)}>上一页</Button>
      <span>第 {pagination.page} 页，共 {pagination.totalPages} 页</span>
      <Button type="button" variant="secondary" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)}>下一页</Button>
    </nav>}
  </>;
}

type AdminTaskDetail = {
  task: AdminTaskListItem & {
    currentStage?: string;
    requestedPageCount?: number;
    currentDeckVersionPublicId?: string;
    confirmedDeckVersionPublicId?: string;
    createdAt: string;
    archivedAt?: string;
  };
  adminUser: { id: number; publicId: string; phone?: string; nickname: string; tier: string; status: string };
  adminActions: { canRetryOrContinue: boolean; operation?: string; label?: string; description?: string };
  sourceFactsVersion?: { id: string; version: number; document?: { sources?: unknown[]; facts?: unknown[]; metrics?: unknown[] }; summary?: { sourceCount?: number; factCount?: number; metricCount?: number } } | null;
  deckVersion?: { id: string; version: number; document?: { title?: string; slides?: Array<{ id?: string; title?: string; purpose?: string }> } } | null;
  deckVersions?: Array<{ id: string; version: number; status: string; changeSummary?: string; createdBy?: string; createdAt?: string; confirmedAt?: string }>;
  generations?: Array<{ id: string; productCode?: string; status: string; creditCost?: number; modelCallCount?: number; inputTokens?: number; outputTokens?: number; modelCostFen?: number; createdAt?: string; startedAt?: string; finishedAt?: string }>;
  renders?: Array<{ id: string; renderer?: string; productCode?: string; status: string; attemptNo?: number; errorCode?: string; errorMessage?: string; previewUrl?: string; downloadUrl?: string; createdAt?: string; updatedAt?: string; outputManifest?: { slides?: unknown[] } }>;
  workerTasks?: Array<{ id: string; taskType?: string; status: string; workerId?: string; progressPercent?: number; progressText?: string; attemptNo?: number; errorCode?: string; errorMessage?: string; queuedAt?: string; startedAt?: string; finishedAt?: string; updatedAt?: string }>;
  slideProgress?: Array<{ renderId?: string; slideIndex?: number; status?: string; progressPercent?: number; progressText?: string; errorMessage?: string }>;
  modelUsage?: { callCount?: number; inputTokens?: number; outputTokens?: number; costFen?: number; latencyMs?: number };
};

export function AdminProjectDetailPage() {
  const auth = useAdminAuth();
  const { taskId = "" } = useParams();
  const [detail, setDetail] = useState<AdminTaskDetail>();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    setError("");
    try {
      const data = await adminApi<AdminTaskDetail>(`/api/admin/ppt-tasks/${encodeURIComponent(taskId)}`);
      setDetail(data);
    } catch (caught) {
      setDetail(undefined);
      setError(caught instanceof Error ? caught.message : "任务详情加载失败");
    }
  }, [taskId]);
  useEffect(() => { void loadDetail(); }, [loadDetail]);

  const retryOrContinue = async () => {
    if (!detail?.adminActions.canRetryOrContinue || !auth.can("projects.operate")) return;
    const label = detail.adminActions.label || "重试 / 继续";
    if (!window.confirm(`确认对任务 ${detail.task.id} 执行“${label}”吗？\n\n${detail.adminActions.description || ""}`)) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const idempotencyKey = `admin:${detail.adminActions.operation || "retry"}:${detail.task.id}:${Date.now()}`;
      await adminApi(`/api/admin/ppt-tasks/${encodeURIComponent(detail.task.id)}/retry`, {
        method: "POST",
        body: JSON.stringify({ idempotencyKey }),
      });
      setNotice(`已提交“${label}”，任务状态会在 Worker/规划进程接管后更新。`);
      await loadDetail();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "任务操作失败");
    } finally {
      setBusy(false);
    }
  };

  if (!detail && !error) return <LoadingBlock/>;
  if (!detail) return <><PageTitle eyebrow="任务运维" title="任务详情不可用" description={error || "任务不存在"} action={<ButtonLink variant="secondary" to={`${ADMIN_BASE_PATH}/projects`}>返回列表</ButtonLink>}/></>;
  const { task, adminUser } = detail;
  const sourceDocument = detail.sourceFactsVersion?.document;
  const sourceSummary = detail.sourceFactsVersion?.summary;
  const slides = detail.deckVersion?.document?.slides || [];
  const failedSlides = (detail.slideProgress || []).filter((slide) => slide.status === "failed");
  const operationAllowed = detail.adminActions.canRetryOrContinue && auth.can("projects.operate");
  return <>
    <PageTitle
      eyebrow="任务运维 · PPT 详情"
      title={task.title || "未命名任务"}
      description={`${task.id} · 数据库 #${task.numericId} · ${adminUser.phone || "未绑定手机号"}`}
      action={<div className="admin-task-detail-actions"><ButtonLink variant="secondary" to={`${ADMIN_BASE_PATH}/projects`}>返回列表</ButtonLink>{auth.can("projects.operate") && <ButtonLink variant="secondary" to={`${ADMIN_BASE_PATH}/projects/${encodeURIComponent(task.id)}/data`} target="_blank" rel="noreferrer">详细数据</ButtonLink>}<Button type="button" variant="secondary" onClick={() => void loadDetail()}>刷新</Button>{operationAllowed && <Button type="button" disabled={busy} onClick={() => void retryOrContinue()}>{busy ? "提交中…" : detail.adminActions.label || "重试 / 继续"}</Button>}</div>}
    />
    {error && <div className="form-error admin-task-message" role="alert">{error}</div>}
    {notice && <div className="form-notice admin-task-message" role="status">{notice}</div>}
    <section className={`admin-task-status-card ${task.status === "failed" ? "is-failed" : ""}`}>
      <div><Badge tone={taskStatusTone(task.status)}>{taskStatusLabel(task.status)}</Badge><strong>{task.progressPercent ?? 0}%</strong><span>{task.progressText || "暂无进度说明"}</span></div>
      <dl><div><dt>当前阶段</dt><dd>{task.currentStage || "–"}</dd></div><div><dt>生成模式</dt><dd>{taskModeLabel(task.generationMode)}</dd></div><div><dt>重试次数</dt><dd>{task.retryCount || 0}</dd></div><div><dt>计划页数</dt><dd>{task.requestedPageCount || slides.length || "–"}</dd></div></dl>
    </section>
    {(task.errorMessage || failedSlides.length > 0) && <section className="admin-task-diagnostic" aria-labelledby="task-diagnostic-title">
      <div><Badge tone="danger">失败诊断</Badge><h2 id="task-diagnostic-title">{task.errorCode || task.failureStage || "生成过程出现异常"}</h2><p>{task.errorMessage || `${failedSlides.length} 页渲染失败`}</p></div>
      <dl><div><dt>失败阶段</dt><dd>{task.failureStage || "–"}</dd></div><div><dt>失败页</dt><dd>{failedSlides.length ? failedSlides.map((slide) => Number(slide.slideIndex) + 1).join("、") : "–"}</dd></div></dl>
    </section>}
    <div className="admin-task-detail-grid">
      <section className="content-section"><div className="section-row"><div><h2>任务与用户</h2><p>用于确认归属、状态和创建时间。</p></div></div><dl className="admin-task-kv"><div><dt>用户</dt><dd>{adminUser.nickname} · {adminUser.tier}</dd></div><div><dt>手机号</dt><dd>{adminUser.phone || "未绑定"}</dd></div><div><dt>用户 ID</dt><dd>{adminUser.publicId} · #{adminUser.id}</dd></div><div><dt>任务 ID</dt><dd>{task.id} · #{task.numericId}</dd></div><div><dt>创建时间</dt><dd>{formatDate(task.createdAt)}</dd></div><div><dt>更新时间</dt><dd>{formatDate(task.updatedAt)}</dd></div></dl></section>
      <section className="content-section"><div className="section-row"><div><h2>资料与大纲</h2><p>这里只显示计数和 PPT 页标题，不读取或展开原始长文本。</p></div></div><dl className="admin-task-kv"><div><dt>资料版本</dt><dd>{detail.sourceFactsVersion ? `v${detail.sourceFactsVersion.version}` : "–"}</dd></div><div><dt>来源 / 事实 / 指标</dt><dd>{sourceSummary?.sourceCount ?? sourceDocument?.sources?.length ?? 0} / {sourceSummary?.factCount ?? sourceDocument?.facts?.length ?? 0} / {sourceSummary?.metricCount ?? sourceDocument?.metrics?.length ?? 0}</dd></div><div><dt>大纲版本</dt><dd>{detail.deckVersion ? `v${detail.deckVersion.version}` : "–"}</dd></div><div><dt>PPT 页数</dt><dd>{slides.length}</dd></div></dl></section>
      <section className="content-section"><div className="section-row"><div><h2>模型用量</h2><p>当前项目累计的模型调用数据。</p></div></div><dl className="admin-task-kv"><div><dt>调用次数</dt><dd>{detail.modelUsage?.callCount || 0}</dd></div><div><dt>输入 Token</dt><dd>{formatNumber(detail.modelUsage?.inputTokens)}</dd></div><div><dt>输出 Token</dt><dd>{formatNumber(detail.modelUsage?.outputTokens)}</dd></div><div><dt>模型成本</dt><dd>¥ {((detail.modelUsage?.costFen || 0) / 100).toFixed(2)}</dd></div><div><dt>累计延迟</dt><dd>{formatDuration(detail.modelUsage?.latencyMs)}</dd></div></dl></section>
    </div>
    <section className="content-section admin-task-section"><div className="section-row"><div><h2>PPT 大纲</h2><p>{slides.length ? `当前版本共 ${slides.length} 页` : "当前任务尚未生成大纲"}</p></div></div>{slides.length ? <ol className="admin-outline-list">{slides.map((slide, index) => <li key={slide.id || index}><b>{index + 1}</b><div><strong>{slide.title || `第 ${index + 1} 页`}</strong>{slide.purpose && <span>{slide.purpose}</span>}</div></li>)}</ol> : <EmptyState title="暂无 PPT 大纲" description="规划成功后会在这里显示每页标题。"/>}</section>
    <section className="content-section admin-task-section"><div className="section-row"><div><h2>Generation 与 Render</h2><p>用于判断策略选择、执行次数、每次生成的 Token 和最终产物。</p></div></div><div className="data-table admin-task-history-table"><div className="data-row data-head"><span>类型 / ID</span><span>产品 / Renderer</span><span>状态</span><span>尝试 / 页数</span><span>失败信息</span><span>时间</span><span>产物</span></div>{(detail.generations || []).map((generation) => <div className="data-row" key={`g-${generation.id}`}><strong>Generation<small>{generation.id}</small></strong><span>{generation.productCode || "–"}</span><span><Badge tone={taskStatusTone(generation.status)}>{taskStatusLabel(generation.status)}</Badge></span><span>{generation.modelCallCount || 0} 次调用<small>Token {formatNumber((generation.inputTokens || 0) + (generation.outputTokens || 0))}（入 {formatNumber(generation.inputTokens)} / 出 {formatNumber(generation.outputTokens)}）</small></span><span>–</span><span>{formatDate(generation.finishedAt || generation.startedAt || generation.createdAt)}</span><span>–</span></div>)}{(detail.renders || []).map((render) => <div className="data-row" key={`r-${render.id}`}><strong>Render<small>{render.id}</small></strong><span>{render.renderer || "–"}<small>{render.productCode}</small></span><span><Badge tone={taskStatusTone(render.status)}>{taskStatusLabel(render.status)}</Badge></span><span>第 {render.attemptNo || 1} 次 · {render.outputManifest?.slides?.length || 0} 页</span><span className={render.errorMessage ? "admin-task-error" : "admin-task-muted"}>{render.errorCode || "–"}<small>{render.errorMessage || "无失败信息"}</small></span><span>{formatDate(render.updatedAt || render.createdAt)}</span><span className="admin-task-artifacts">{render.previewUrl && <a href={render.previewUrl} target="_blank" rel="noreferrer">预览</a>}{render.downloadUrl && <a href={render.downloadUrl}>下载 PPTX</a>}{!render.previewUrl && !render.downloadUrl && "–"}</span></div>)}</div></section>
    <section className="content-section admin-task-section"><div className="section-row"><div><h2>Worker 执行记录</h2><p>最近 20 次与该项目直接关联的后台任务，不读取任务 payload。</p></div></div>{detail.workerTasks?.length ? <div className="data-table admin-worker-history-table"><div className="data-row data-head"><span>Worker 任务</span><span>类型</span><span>状态</span><span>进度</span><span>尝试</span><span>失败信息</span><span>更新时间</span></div>{detail.workerTasks.map((worker) => <div className="data-row" key={worker.id}><strong>{worker.id}<small>{worker.workerId || "尚未领取"}</small></strong><span>{worker.taskType || "–"}</span><span><Badge tone={taskStatusTone(worker.status)}>{taskStatusLabel(worker.status)}</Badge></span><span>{worker.progressPercent || 0}%<small>{worker.progressText}</small></span><span>{worker.attemptNo || 0}</span><span className={worker.errorMessage ? "admin-task-error" : "admin-task-muted"}>{worker.errorCode || "–"}<small>{worker.errorMessage || "无失败信息"}</small></span><span>{formatDate(worker.updatedAt)}</span></div>)}</div> : <EmptyState title="暂无关联 Worker 记录" description="本地执行的任务或历史未关联 project_id 的任务可能不会显示记录。"/>}</section>
  </>;
}

function taskStatusLabel(status: string) {
  const labels: Record<string, string> = { draft: "草稿", parsing: "解析中", planning: "规划中", waiting_outline: "待确认大纲", ready_to_generate: "待生成", queued: "排队中", running: "执行中", generating: "生成中", rendering: "渲染中", succeeded: "成功", failed: "失败", cancelled: "已取消", partial: "部分成功" };
  return labels[status] || status || "未知";
}
function taskStatusTone(status: string): "neutral" | "brand" | "success" | "warning" | "danger" {
  if (status === "succeeded") return "success";
  if (status === "failed") return "danger";
  if (["planning", "queued", "running", "generating", "rendering", "parsing"].includes(status)) return "brand";
  if (["cancelled", "partial", "waiting_outline", "ready_to_generate"].includes(status)) return "warning";
  return "neutral";
}
function taskModeLabel(mode?: string) { return mode === "image" ? "视觉模式" : mode === "html" ? "HTML 模式" : "标准模式"; }
function formatNumber(value?: number) { return new Intl.NumberFormat("zh-CN").format(value || 0); }
function formatDuration(value?: number) { if (!value) return "0 秒"; return value >= 60_000 ? `${(value / 60_000).toFixed(1)} 分钟` : `${(value / 1_000).toFixed(1)} 秒`; }

export function AdminLabPage(){const auth=useAdminAuth();const tester=auth.auth?.session.role==="tester";return <><PageTitle eyebrow="PPT 实验室" title={tester?"PPT 实验功能":"内部策略验证"} description={tester?"在独立测试空间体验即将开放的 PPT 生成与美化能力。":"Admin 可使用同一 DeckPlan 对比 renderer、模型路由和实验参数。"}/><div className="lab-banner"><Badge tone="warning">{tester?"Tester":"Internal only"}</Badge><h2>{tester?"实验功能将在这里逐步开放":"实验功能先在这里验证，再开放给 Tester"}</h2><p>{tester?"测试操作与正式项目隔离；可用能力会根据实验进度逐步增加。":"所有强制 renderer 与策略覆盖都会保存到 generation 决策快照并进入审计日志。"}</p></div><div className="settings-grid"><section className="settings-card"><h2>当前权限</h2><p>{auth.auth?.session.role}</p><dl><div><dt>PPT 实验室</dt><dd>{auth.can("lab.use")?"允许":"禁止"}</dd></div><div><dt>主题测试</dt><dd>{auth.can("themes.test")||auth.can("models.manage")?"允许":"禁止"}</dd></div></dl></section><section className="settings-card"><h2>实验入口</h2><p>完整 DeckPlan 选择、多路并排对比和单页重跑在阶段 J 实现。</p><Button disabled>创建内部实验</Button></section></div></>;}

type AdminUser = {
  id: number;
  publicId: string;
  nickname: string;
  phone?: string;
  tier: "normal" | "tester";
  status: string;
  registeredSource: string;
  closureRequestedAt?: string;
  closedAt?: string;
};

export function AdminUsersPage() {
  const auth = useAdminAuth();
  const [testers, setTesters] = useState<AdminUser[]>();
  const [phone, setPhone] = useState("");
  const [results, setResults] = useState<AdminUser[]>();
  const [searchedPhone, setSearchedPhone] = useState("");
  const [busyUserId, setBusyUserId] = useState<number>();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [closingUsers, setClosingUsers] = useState<AdminUser[]>();
  const [accountClosureEnabled, setAccountClosureEnabled] = useState(false);
  const [closureCandidate, setClosureCandidate] = useState<AdminUser>();

  const loadTesters = useCallback(async () => {
    const data = await adminApi<{ users: AdminUser[]; accountClosureEnabled?: boolean }>("/api/admin/users");
    setTesters(data.users || []);
    setAccountClosureEnabled(data.accountClosureEnabled === true);
  }, []);

  useEffect(() => {
    void loadTesters().catch((caught) => {
      setTesters([]);
      setError(caught instanceof Error ? caught.message : "Tester 列表加载失败");
    });
  }, [loadTesters]);

  const loadClosingUsers = useCallback(async () => {
    const data = await adminApi<{ users: AdminUser[] }>("/api/admin/users?status=closing");
    setClosingUsers(data.users || []);
  }, []);

  useEffect(() => {
    if (accountClosureEnabled) void loadClosingUsers().catch((caught) => setError(caught instanceof Error ? caught.message : "注销申请列表加载失败"));
  }, [accountClosureEnabled, loadClosingUsers]);

  const search = async (event: FormEvent) => {
    event.preventDefault();
    const value = phone.replace(/[\s-]/g, "");
    if (!value) {
      setResults(undefined);
      setSearchedPhone("");
      setError("");
      return;
    }
    setError("");
    setNotice("");
    try {
      const data = await adminApi<{ users: AdminUser[] }>(`/api/admin/users?phone=${encodeURIComponent(value)}`);
      setResults(data.users || []);
      setSearchedPhone(value);
    } catch (caught) {
      setResults([]);
      setSearchedPhone(value);
      setError(caught instanceof Error ? caught.message : "手机号搜索失败");
    }
  };

  const setTester = async (user: AdminUser, enabled: boolean) => {
    setBusyUserId(user.id);
    setError("");
    setNotice("");
    try {
      const data = await adminApi<{ user: AdminUser }>(`/api/admin/users/${user.id}/access`, {
        method: "PUT",
        body: JSON.stringify({ userType: enabled ? "tester" : "normal" }),
      });
      setResults((current) => current?.map((item) => item.id === user.id ? data.user : item));
      await loadTesters();
      setNotice(enabled ? `已将 ${user.phone || user.nickname} 加入 Tester` : `已将 ${user.phone || user.nickname} 移出 Tester`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Tester 权限更新失败");
    } finally {
      setBusyUserId(undefined);
    }
  };

  const confirmClosure = async () => {
    if (!closureCandidate) return;
    const user = closureCandidate;
    setBusyUserId(user.id); setError(""); setNotice("");
    try {
      await adminApi(`/api/admin/users/${user.id}/closure/confirm`, { method: "POST", body: JSON.stringify({ confirm: true }) });
      setClosureCandidate(undefined);
      setClosingUsers((current) => current?.filter((item) => item.id !== user.id));
      setResults((current) => current?.map((item) => item.id === user.id ? { ...item, status: "closed" } : item));
      await loadTesters();
      setNotice(`已确认注销 ${user.phone || user.nickname}`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "确认注销失败"); }
    finally { setBusyUserId(undefined); }
  };

  const canManage = auth.can("admin.manage");
  return <>
    <PageTitle eyebrow="用户与 Tester" title="Tester 管理" description="默认仅显示 Tester。通过手机号查找其他用户，并加入或移出 Tester。"/>
    <form className="admin-user-search" onSubmit={search}>
      <label>
        <span>搜索手机号</span>
        <input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" maxLength={13} placeholder="输入手机号或连续数字"/>
      </label>
      <Button type="submit" variant="secondary">搜索用户</Button>
    </form>
    {error && <div className="form-error admin-user-message">{error}</div>}
    {notice && <div className="form-notice admin-user-message">{notice}</div>}
    {results && <section className="admin-user-results">
      <div className="section-row"><div><h2>搜索结果</h2><p>手机号包含“{searchedPhone}”</p></div></div>
      {results.length ? <AdminUserTable users={results} canManage={canManage} busyUserId={busyUserId} onSetTester={setTester} onConfirmClosure={setClosureCandidate}/>
        : <EmptyState title="未找到用户" description="请检查手机号是否正确，用户需要先完成注册。"/>}
    </section>}
    {accountClosureEnabled && <section className="admin-user-list"><div className="section-row"><div><h2>待确认注销</h2><p>账户已禁止登录和注册，管理员确认后将正式注销。</p></div></div>{!closingUsers?<LoadingBlock/>:closingUsers.length?<AdminUserTable users={closingUsers} canManage={canManage} busyUserId={busyUserId} onSetTester={setTester} onConfirmClosure={setClosureCandidate}/>:<EmptyState title="暂无待确认注销账户" description="新的注销申请会显示在这里。"/>}</section>}
    <section className="admin-user-list">
      <div className="section-row"><div><h2>当前 Tester</h2><p>共 {testers?.length ?? "–"} 个账户</p></div></div>
      {!testers ? <LoadingBlock/> : testers.length
        ? <AdminUserTable users={testers} canManage={canManage} busyUserId={busyUserId} onSetTester={setTester} onConfirmClosure={setClosureCandidate}/>
        : <EmptyState title="暂无 Tester" description="通过上方手机号搜索，将已注册用户加入 Tester。"/>}
    </section>
    {closureCandidate && <AdminClosureConfirmDialog user={closureCandidate} busy={busyUserId === closureCandidate.id} onClose={() => !busyUserId && setClosureCandidate(undefined)} onConfirm={() => void confirmClosure()}/>}
  </>;
}

function AdminUserTable({ users, canManage, busyUserId, onSetTester, onConfirmClosure }: {
  users: AdminUser[];
  canManage: boolean;
  busyUserId?: number;
  onSetTester: (user: AdminUser, enabled: boolean) => Promise<void>;
  onConfirmClosure: (user: AdminUser) => void;
}) {
  return <div className="data-table admin-user-table">
    <div className="data-row data-head"><span>用户</span><span>手机号</span><span>权限</span><span>操作</span></div>
    {users.map((user) => <div className="data-row" key={user.publicId}>
      <strong>{user.nickname}<small>{userStatusLabel(user.status)}</small></strong>
      <span className="admin-user-phone">{user.phone || "未绑定"}</span>
      <span><Badge tone={user.tier === "tester" ? "warning" : "neutral"}>{user.tier === "tester" ? "Tester" : "普通用户"}</Badge></span>
      <span>{user.status === "closing" ? <Button type="button" variant="danger" disabled={!canManage || busyUserId === user.id} onClick={() => onConfirmClosure(user)}>{busyUserId === user.id ? "处理中…" : "确认注销"}</Button> : <Button
        type="button"
        variant={user.tier === "tester" ? "danger" : "secondary"}
        disabled={!canManage || busyUserId === user.id || user.status === "closed"}
        onClick={() => void onSetTester(user, user.tier !== "tester")}
      >{busyUserId === user.id ? "处理中…" : user.tier === "tester" ? "移出 Tester" : "加入 Tester"}</Button>}</span>
    </div>)}
  </div>;
}

type CreditPricing = {
  revision: number;
  masterAiGeneratePerSlide: number;
  image2GeneratePerSlide: number;
  image2RevisionPerSlide: number;
};

type CreditAccount = {
  userId: number;
  balance: number;
  totalGranted: number;
  totalConsumed: number;
  totalRefunded: number;
};

type CreditTransaction = {
  id: string;
  userId: number;
  userNickname?: string;
  kind: "grant" | "consume" | "refund";
  reasonCode: string;
  delta: number;
  balanceAfter: number;
  note?: string;
  createdAt: string;
};

const defaultCreditPricing: CreditPricing = {
  revision: 1,
  masterAiGeneratePerSlide: 6,
  image2GeneratePerSlide: 20,
  image2RevisionPerSlide: 10,
};

export function AdminCreditsPage() {
  const auth = useAdminAuth();
  const [pricing, setPricing] = useState<CreditPricing>(defaultCreditPricing);
  const [transactions, setTransactions] = useState<CreditTransaction[]>();
  const [phone, setPhone] = useState("");
  const [users, setUsers] = useState<AdminUser[]>();
  const [selectedUser, setSelectedUser] = useState<AdminUser>();
  const [account, setAccount] = useState<CreditAccount>();
  const [points, setPoints] = useState(100);
  const [reason, setReason] = useState("内测赠送");
  const [busy, setBusy] = useState<"pricing" | "grant">();
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadTransactions = useCallback(async (userId?: number) => {
    const query = new URLSearchParams({ limit: "100" });
    if (userId) query.set("userId", String(userId));
    const data = await adminApi<{ transactions: CreditTransaction[] }>(`/api/admin/credits/transactions?${query}`);
    setTransactions(data.transactions || []);
  }, []);

  useEffect(() => {
    adminApi<{ pricing: CreditPricing }>("/api/admin/credits/pricing")
      .then((data) => setPricing(data.pricing))
      .catch((caught) => setError(caught instanceof Error ? caught.message : "积分价格加载失败"));
    void loadTransactions().catch(() => setTransactions([]));
  }, [loadTransactions]);

  const searchUser = async () => {
    const value = phone.replace(/[\s-]/g, "");
    setError("");
    setNotice("");
    setSelectedUser(undefined);
    setAccount(undefined);
    if (!value) {
      setUsers([]);
      return;
    }
    try {
      const data = await adminApi<{ users: AdminUser[] }>(`/api/admin/users?phone=${encodeURIComponent(value)}`);
      setUsers(data.users || []);
    } catch (caught) {
      setUsers([]);
      setError(caught instanceof Error ? caught.message : "用户搜索失败");
    }
  };

  const selectUser = async (user: AdminUser) => {
    setSelectedUser(user);
    setError("");
    try {
      const data = await adminApi<{ account: CreditAccount }>(`/api/admin/credits/accounts/${user.id}`);
      setAccount(data.account);
      await loadTransactions(user.id);
    } catch (caught) {
      setAccount(undefined);
      setError(caught instanceof Error ? caught.message : "积分账户加载失败");
    }
  };

  const savePricing = async () => {
    setBusy("pricing");
    setError("");
    setNotice("");
    try {
      const values = {
        masterAiGeneratePerSlide: Number(pricing.masterAiGeneratePerSlide),
        image2GeneratePerSlide: Number(pricing.image2GeneratePerSlide),
        image2RevisionPerSlide: Number(pricing.image2RevisionPerSlide),
      };
      if (Object.values(values).some((value) => !Number.isSafeInteger(value) || value < 0)) {
        throw new Error("每项价格必须是大于或等于 0 的整数");
      }
      const data = await adminApi<{ pricing: CreditPricing }>("/api/admin/credits/pricing", {
        method: "PUT",
        body: JSON.stringify({ pricing: values }),
      });
      setPricing(data.pricing);
      setNotice(`积分价格已保存为第 ${data.pricing.revision} 版，只影响之后创建的生成任务。`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "积分价格保存失败");
    } finally {
      setBusy(undefined);
    }
  };

  const grant = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedUser) {
      setError("请先搜索并选择用户");
      return;
    }
    setBusy("grant");
    setError("");
    setNotice("");
    try {
      if (!Number.isSafeInteger(points) || points <= 0) throw new Error("赠送积分必须是正整数");
      const data = await adminApi<{ account: CreditAccount }>("/api/admin/credits/grants", {
        method: "POST",
        body: JSON.stringify({
          userId: selectedUser.id,
          points,
          reason: reason.trim() || "内测赠送",
          idempotencyKey: `admin-grant:${selectedUser.id}:${Date.now()}`,
        }),
      });
      setAccount(data.account);
      setNotice(`已向 ${selectedUser.phone || selectedUser.nickname} 赠送 ${points} 积分。`);
      await loadTransactions(selectedUser.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "赠送积分失败");
    } finally {
      setBusy(undefined);
    }
  };

  const canManage = auth.can("credits.manage");
  return <>
    <PageTitle eyebrow="内测积分" title="积分管理" description="内测期间不开放充值；这里只维护赠送积分、消耗价格和不可变流水。"/>
    {error && <div className="form-error admin-user-message">{error}</div>}
    {notice && <div className="form-notice admin-user-message">{notice}</div>}
    <div className="settings-grid credit-settings-grid">
      <section className="settings-card">
        <div className="credit-card-head"><div><h2>消耗价格</h2><p>按实际生成页数计费；修改价格不追溯已有任务。</p></div><Badge tone="brand">版本 {pricing.revision}</Badge></div>
        <div className="credit-pricing-fields">
          <label><span>大师 AI 生成</span><input type="number" min={0} value={pricing.masterAiGeneratePerSlide} onChange={(event) => setPricing((current) => ({...current, masterAiGeneratePerSlide: Number(event.target.value)}))}/><small>积分 / 页</small></label>
          <label><span>Image2 生成</span><input type="number" min={0} value={pricing.image2GeneratePerSlide} onChange={(event) => setPricing((current) => ({...current, image2GeneratePerSlide: Number(event.target.value)}))}/><small>积分 / 页</small></label>
          <label><span>Image2 修改</span><input type="number" min={0} value={pricing.image2RevisionPerSlide} onChange={(event) => setPricing((current) => ({...current, image2RevisionPerSlide: Number(event.target.value)}))}/><small>积分 / 页</small></label>
        </div>
        <Button disabled={!canManage || busy === "pricing"} onClick={() => void savePricing()}>{busy === "pricing" ? "保存中…" : "保存价格"}</Button>
      </section>
      <section className="settings-card">
        <div className="credit-card-head"><div><h2>赠送积分</h2><p>先按手机号找到用户，再录入本次内测赠送。</p></div>{account && <Badge tone="success">余额 {account.balance}</Badge>}</div>
        <form className="credit-grant-form" onSubmit={grant}>
          <div className="credit-user-search"><input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" placeholder="输入手机号"/><Button type="button" variant="secondary" onClick={() => void searchUser()}>搜索</Button></div>
          {users && <div className="credit-user-results">{users.length ? users.map((user) => <button type="button" className={selectedUser?.id === user.id ? "selected" : ""} key={user.publicId} onClick={() => void selectUser(user)}><strong>{user.nickname}</strong><span>{user.phone || "未绑定手机号"} · ID {user.id}</span></button>) : <small>没有找到已注册用户</small>}</div>}
          <label><span>赠送积分</span><input type="number" min={1} value={points} onChange={(event) => setPoints(Number(event.target.value))}/></label>
          <label><span>备注</span><input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="例如：首轮内测赠送"/></label>
          <Button type="submit" disabled={!canManage || !selectedUser || busy === "grant"}>{busy === "grant" ? "赠送中…" : "确认赠送"}</Button>
        </form>
      </section>
    </div>
    <section className="credit-ledger-section">
      <div className="section-row"><div><h2>{selectedUser ? `${selectedUser.nickname} 的积分流水` : "最近流水"}</h2><p>{selectedUser ? `用户 ID ${selectedUser.id} · 当前余额 ${account?.balance ?? "–"}` : "展示最近 100 条赠送、消耗和失败退款记录。"}</p></div>{selectedUser && <Button variant="ghost" onClick={() => { setSelectedUser(undefined); setAccount(undefined); void loadTransactions(); }}>查看全部流水</Button>}</div>
      {!transactions ? <LoadingBlock/> : transactions.length ? <div className="data-table credit-ledger-table">
        <div className="data-row data-head"><span>用户</span><span>类型</span><span>变动</span><span>余额</span><span>原因</span><span>时间</span></div>
        {transactions.map((item) => <div className="data-row" key={item.id}>
          <strong>{item.userNickname || `用户 ${item.userId}`}<small>ID {item.userId}</small></strong>
          <span><Badge tone={item.kind === "consume" ? "warning" : "success"}>{creditKindLabel(item.kind)}</Badge></span>
          <span className={item.delta > 0 ? "credit-positive" : "credit-negative"}>{item.delta > 0 ? "+" : ""}{item.delta}</span>
          <span>{item.balanceAfter}</span>
          <span title={item.note}>{creditReasonLabel(item.reasonCode)}</span>
          <span>{formatDate(item.createdAt)}</span>
        </div>)}
      </div> : <EmptyState title="暂无积分流水" description="管理员首次赠送积分后会显示在这里。"/>}
    </section>
  </>;
}

export function AdminSystemPage(){
  const [policy,setPolicy]=useState<Record<string,unknown>>();
  const [editionInfo,setEditionInfo]=useState<EditionAdminInfo>();
  const [catalog,setCatalog]=useState<Record<string,unknown>>();
  const [modelPool,setModelPool]=useState<PptModelPool>(()=>defaultModelPool());
  const [modelConfigSource,setModelConfigSource]=useState<"process.env"|".env.development">("process.env");
  const [image2Settings,setImage2Settings]=useState<Image2Settings>({resolution:"1672x941"});
  const [image2Presets,setImage2Presets]=useState<Image2ResolutionPreset[]>(defaultImage2Presets());
  const [image2Status,setImage2Status]=useState("");
  const [editableExportSettings,setEditableExportSettings]=useState<EditableExportSettings>(defaultEditableExportSettings());
  const [editableExportStatus,setEditableExportStatus]=useState("");
  const [uploadLimits,setUploadLimits]=useState<PptGenerationUploadLimits>(defaultPptGenerationUploadLimits());
  const [uploadLimitsStatus,setUploadLimitsStatus]=useState("");
  const [storageQuota,setStorageQuota]=useState<StorageQuotaPolicy>(defaultStorageQuotaPolicy());
  const [storageQuotaStatus,setStorageQuotaStatus]=useState("");
  const [chinaReviewApi,setChinaReviewApi]=useState<ChinaReviewApiAdminSettings>(()=>defaultChinaReviewApiSettings());
  const [chinaReviewApiPassword,setChinaReviewApiPassword]=useState("");
  const [chinaReviewApiAvailable,setChinaReviewApiAvailable]=useState(false);
  const [chinaReviewApiStatus,setChinaReviewApiStatus]=useState("");
  const [chinaReviewApiSaving,setChinaReviewApiSaving]=useState(false);
  const [ocrStatus,setOcrStatus]=useState<OcrServiceConfiguration>();
  const [ocrTest,setOcrTest]=useState<OcrServiceTestResult>();
  const [ocrTesting,setOcrTesting]=useState(false);
  const [modelTests,setModelTests]=useState<Record<string,ModelTestResult>>({});
  const [modelStatus,setModelStatus]=useState("");
  const [modelStatusTone,setModelStatusTone]=useState<"neutral"|"success"|"error">("neutral");
  const [modelSaving,setModelSaving]=useState(false);
  const [modelHealth,setModelHealth]=useState<ModelHealthSnapshot[]>();
  const [modelHealthEvents,setModelHealthEvents]=useState<ModelHealthEvent[]>();
  const [modelHealthCategory,setModelHealthCategory]=useState<""|ModelRouteCategory>("");
  const [modelHealthModelId,setModelHealthModelId]=useState("");
  const [modelHealthStatus,setModelHealthStatus]=useState("");
  const [recoveringModelKey,setRecoveringModelKey]=useState("");
  const loadModelHealth=async()=>{
    const [healthResult,eventResult]=await Promise.all([
      adminApi<{health:ModelHealthSnapshot[]}>("/api/admin/ppt/model-health"),
      adminApi<{events:ModelHealthEvent[]}>("/api/admin/ppt/model-health/events?limit=500"),
    ]);
    setModelHealth(healthResult.health);
    setModelHealthEvents(eventResult.events);
  };

  useEffect(()=>{
    adminApi<{policy:Record<string,unknown>;edition:EditionAdminInfo["edition"];deploymentId:string}>("/api/admin/features").then((d)=>{
      setPolicy(d.policy);
      setEditionInfo({edition:d.edition,deploymentId:d.deploymentId});
    }).catch(()=>setPolicy({}));
    adminApi<{catalog:Record<string,unknown>}>("/api/admin/styles").then((d)=>setCatalog(d.catalog)).catch(()=>setCatalog({}));
    adminApi<{modelPool:PptModelPool;source:"process.env"|".env.development"}>("/api/admin/ppt/model-pool").then((d)=>{
      setModelPool(normalizeModelPool(d.modelPool));
      setModelConfigSource(d.source);
    }).catch(()=>setModelPool(defaultModelPool()));
    void loadModelHealth().catch(()=>{
      setModelHealth([]);
      setModelHealthEvents([]);
      setModelHealthStatus("模型状态暂时无法加载，请稍后刷新。");
    });
    adminApi<{settings:Image2Settings;presets:Image2ResolutionPreset[]}>("/api/admin/ppt/image2-settings").then((d)=>{
      setImage2Settings(d.settings);
      setImage2Presets(d.presets);
    }).catch(()=>setImage2Settings({resolution:"1672x941"}));
    adminApi<{settings:EditableExportSettings}>("/api/admin/ppt/editable-export-settings")
      .then((d)=>setEditableExportSettings(d.settings))
      .catch(()=>setEditableExportSettings(defaultEditableExportSettings()));
    adminApi<{limits:PptGenerationUploadLimits}>("/api/admin/ppt/upload-limits")
      .then((d)=>setUploadLimits(d.limits))
      .catch(()=>setUploadLimits(defaultPptGenerationUploadLimits()));
    adminApi<{policy:StorageQuotaPolicy}>("/api/admin/ppt/storage-quota")
      .then((d)=>setStorageQuota(d.policy))
      .catch(()=>setStorageQuota(defaultStorageQuotaPolicy()));
    adminApi<{available:boolean;settings:ChinaReviewApiAdminSettings}>("/api/admin/ppt/china-review-api")
      .then((d)=>{
        setChinaReviewApi(d.settings);
        setChinaReviewApiAvailable(d.available);
      })
      .catch(()=>{
        setChinaReviewApi(defaultChinaReviewApiSettings());
        setChinaReviewApiAvailable(false);
      });
    adminApi<{ocr:OcrServiceConfiguration}>("/api/admin/ppt/ocr-status")
      .then((d)=>setOcrStatus(d.ocr))
      .catch(()=>setOcrStatus(undefined));
  },[]);
  const styles=Array.isArray(catalog?.styles)?catalog.styles as Array<Record<string,unknown>>:[];
  const imageModeEnabled=editionInfo?.edition.capabilities["generation.image_mode"]!==false;
  const ocrEditionEnabled=editionInfo?.edition.capabilities["source.ocr"]!==false;
  const modelConfigReadOnly=modelConfigSource===".env.development";
  const textModels=modelPool.models.map((model,index)=>({model,index})).filter((item)=>item.model.capability==="text");
  const imageModels=modelPool.models.map((model,index)=>({model,index})).filter((item)=>item.model.capability==="image");
  const recoverModel=async(category:ModelRouteCategory,modelId:string)=>{
    const key=`${category}:${modelId}`;
    setRecoveringModelKey(key);
    setModelHealthStatus("");
    try{
      await adminApi(`/api/admin/ppt/model-health/${encodeURIComponent(category)}/${encodeURIComponent(modelId)}/recover`,{method:"POST"});
      await loadModelHealth();
      setModelHealthStatus("已标记为待试用；下一次真实请求会优先验证该模型。" );
    }catch(error){
      setModelHealthStatus(error instanceof Error?`恢复失败：${error.message}`:"恢复失败，请稍后重试。");
    }finally{setRecoveringModelKey("");}
  };
  const updateModel=(index:number,patch:Partial<PptModelPoolModel>)=>{
    setModelPool((current)=>{
      const models=current.models.map((model,itemIndex)=>itemIndex===index?{...model,...patch}:model);
      return {...current,models,defaults:buildModelDefaults(models,current.defaults)};
    });
  };
  const addModel=(capability:PptModelCapability)=>{
    setModelPool((current)=>{
      const models=[...current.models,newModel(capability)];
      return {...current,models,defaults:buildModelDefaults(models,current.defaults)};
    });
  };
  const removeModel=(index:number)=>{
    setModelPool((current)=>{
      const models=current.models.filter((_model,itemIndex)=>itemIndex!==index);
      const removedId=current.models[index]?.id;
      const routes=current.routes?Object.fromEntries(Object.entries(current.routes).map(([category,route])=>[
        category,{candidates:(route?.candidates||[]).filter((modelId)=>modelId!==removedId)},
      ])):undefined;
      return normalizeModelPool({...current,models,defaults:buildModelDefaults(models,current.defaults),...(routes?{routes}:{})});
    });
  };
  const saveModelPool=async()=>{
    if(modelSaving)return;
    setModelSaving(true);
    setModelStatusTone("neutral");
    setModelStatus("正在保存模型池；首次启用 Skill 时可能需要自动上传，请稍候...");
    try{
      const next=normalizeModelPool(modelPool);
      const data=await adminApi<{modelPool:PptModelPool}>("/api/admin/ppt/model-pool",{method:"PUT",body:JSON.stringify({modelPool:next})});
      setModelPool(normalizeModelPool(data.modelPool||next));
      setModelStatusTone("success");
      setModelStatus("模型池保存成功，后续新任务会使用最新配置。");
    }catch(error){
      setModelStatusTone("error");
      setModelStatus(error instanceof Error?`模型池保存失败：${error.message}`:"模型池保存失败，请稍后重试。");
    }finally{
      setModelSaving(false);
    }
  };
  const saveImage2Settings=async()=>{
    setImage2Status("正在保存...");
    const data=await adminApi<{settings:Image2Settings;presets:Image2ResolutionPreset[]}>("/api/admin/ppt/image2-settings",{
      method:"PUT",
      body:JSON.stringify({resolution:image2Settings.resolution}),
    });
    setImage2Settings(data.settings);
    setImage2Presets(data.presets);
    setImage2Status("保存成功，后续新建的 Image2 生成任务会使用该分辨率。");
  };
  const saveEditableExportSettings=async()=>{
    setEditableExportStatus("正在保存...");
    const data=await adminApi<{settings:EditableExportSettings}>("/api/admin/ppt/editable-export-settings",{
      method:"PUT",
      body:JSON.stringify({settings:editableExportSettings}),
    });
    setEditableExportSettings(data.settings);
    setEditableExportStatus("保存成功，导出 Worker 会在 10 秒内应用新配置。");
  };
  const saveUploadLimits=async()=>{
    setUploadLimitsStatus("正在保存...");
    const data=await adminApi<{limits:PptGenerationUploadLimits}>("/api/admin/ppt/upload-limits",{
      method:"PUT",
      body:JSON.stringify({limits:{
        nonMemberMaxTotalMb:uploadLimits.nonMemberMaxTotalMb,
        memberMaxTotalMb:uploadLimits.memberMaxTotalMb,
      }}),
    });
    setUploadLimits(data.limits);
    setUploadLimitsStatus("保存成功，用户重新进入生成页后会使用最新上限。");
  };
  const saveStorageQuota=async()=>{
    setStorageQuotaStatus("正在保存...");
    const data=await adminApi<{policy:StorageQuotaPolicy}>("/api/admin/ppt/storage-quota",{
      method:"PUT",
      body:JSON.stringify({policy:{
        nonMemberQuotaMb:storageQuota.nonMemberQuotaMb,
        memberQuotaMb:storageQuota.memberQuotaMb,
        enforceLimit:storageQuota.enforceLimit,
      }}),
    });
    setStorageQuota(data.policy);
    setStorageQuotaStatus(data.policy.enforceLimit
      ? "保存成功，超限用户的下一次新增内容任务将被阻止。"
      : "保存成功，超限用户仅收到提醒，仍可继续使用。");
  };
  const saveChinaReviewApi=async()=>{
    if(chinaReviewApiSaving)return;
    setChinaReviewApiSaving(true);
    setChinaReviewApiStatus("正在保存测试 API 配置...");
    try{
      const data=await adminApi<{available:boolean;settings:ChinaReviewApiAdminSettings}>("/api/admin/ppt/china-review-api",{
        method:"PUT",
        body:JSON.stringify({settings:{
          enabled:chinaReviewApi.enabled,
          username:chinaReviewApi.username,
          ...(chinaReviewApiPassword?{password:chinaReviewApiPassword}:{}),
          deadlineMs:chinaReviewApi.deadlineMs,
          defaultSlideCount:chinaReviewApi.defaultSlideCount,
          maxSlideCount:chinaReviewApi.maxSlideCount,
          rejectionAnswer:chinaReviewApi.rejectionAnswer,
          publicModelName:chinaReviewApi.publicModelName,
          sourceModelId:chinaReviewApi.sourceModelId,
          additionalSensitiveWords:chinaReviewApi.additionalSensitiveWords,
        }}),
      });
      setChinaReviewApi(data.settings);
      setChinaReviewApiPassword("");
      setChinaReviewApiAvailable(data.available);
      setChinaReviewApiStatus(data.settings.enabled
        ? "保存成功。China 版本的 /test 已启用，运行时只使用本地审核和保底模板。"
        : "保存成功。测试 API 当前保持关闭。");
    }catch(error){
      setChinaReviewApiStatus(error instanceof Error?`保存失败：${error.message}`:"保存失败，请稍后重试。");
    }finally{
      setChinaReviewApiSaving(false);
    }
  };
  const testModel=async(index:number)=>{
    const model=modelPool.models[index];
    if(!model)return;
    setModelTests((current)=>({...current,[model.id]:{ok:true,message:"正在测试连接..."}}));
    try{
      const category:ModelRouteCategory=model.capability==="image"?"image_generation":"text";
      const data=await adminApi<{test:ModelTestResult}>("/api/admin/ppt/model-pool/test",{method:"POST",body:JSON.stringify(modelConfigReadOnly?{modelId:model.id,category}:{model,category})});
      if(data.test.outlineSkill&&!modelConfigReadOnly){
        updateModel(index,{outlineSkill:data.test.outlineSkill});
      }
      setModelTests((current)=>({...current,[model.id]:data.test}));
    }catch(error){
      setModelTests((current)=>({...current,[model.id]:{ok:false,message:error instanceof Error?error.message:"测试失败"}}));
    }
  };
  const testOcr=async()=>{
    setOcrTesting(true);
    setOcrTest(undefined);
    try{
      const data=await adminApi<{ocr:OcrServiceConfiguration;test:OcrServiceTestResult}>("/api/admin/ppt/ocr-status/test",{method:"POST"});
      setOcrStatus(data.ocr);
      setOcrTest(data.test);
    }catch(error){
      setOcrTest({ok:false,status:"unhealthy",message:error instanceof Error?error.message:"OCR 检测失败",latencyMs:0,checkedAt:new Date().toISOString()});
    }finally{
      setOcrTesting(false);
    }
  };
  return <>
    <PageTitle eyebrow="系统配置" title="发布策略、生成规格与模型" description="集中维护 Image2 输出规格、风格目录和 PPT 生成链路使用的大模型配置。"/>
    <div className="settings-grid">
      <section className="settings-card">
        <h2>发行版硬能力</h2>
        <p>这里由服务器发行版配置决定，用户权限和 Feature Policy 不能突破硬关闭。</p>
        <dl>
          <div><dt>当前版本</dt><dd>{editionInfo?.edition.edition==="china"?"中国大陆版":"全球版"}</dd></div>
          <div><dt>配置版本</dt><dd>{editionInfo?.edition.revision||"–"}</dd></div>
          <div><dt>部署标识</dt><dd>{editionInfo?.deploymentId||"–"}</dd></div>
          <div><dt>图片模式</dt><dd>{imageModeEnabled?"允许":"硬关闭"}</dd></div>
          <div><dt>OCR</dt><dd>{ocrEditionEnabled?"允许":"硬关闭"}</dd></div>
        </dl>
      </section>
      <section className="settings-card china-review-api-card">
        <div className="admin-model-config-head">
          <div><h2>China 审核测试 API</h2><p>接口固定为 POST /test。当前策略完全离线，不请求模型、不访问外部网络；先生成保底大纲，再尝试更完整的本地文字大纲。</p></div>
          <Button onClick={()=>void saveChinaReviewApi()} disabled={chinaReviewApiSaving}>{chinaReviewApiSaving?"保存中…":"保存测试 API"}</Button>
        </div>
        <div className="china-review-api-summary">
          <div><span>China 路由</span><strong>{chinaReviewApiAvailable?"可注册":"当前版本不注册"}</strong></div>
          <div><span>接口地址</span><strong>{chinaReviewApi.endpoint}</strong></div>
          <div><span>运行策略</span><strong>离线模板 + 截止前保底</strong></div>
        </div>
        <label className="admin-model-enabled china-review-api-enabled"><input type="checkbox" checked={chinaReviewApi.enabled} disabled={!chinaReviewApi.username.trim()||(!chinaReviewApi.passwordConfigured&&!chinaReviewApiPassword)} onChange={(event)=>setChinaReviewApi((current)=>({...current,enabled:event.target.checked}))}/><span>启用审核测试 API</span></label>
        <div className="china-review-api-fields">
          <label><span>接口用户名</span><input value={chinaReviewApi.username} maxLength={100} autoComplete="off" onChange={(event)=>setChinaReviewApi((current)=>({...current,username:event.target.value}))}/></label>
          <label><span>接口密码</span><input type="password" value={chinaReviewApiPassword} maxLength={200} autoComplete="new-password" onChange={(event)=>setChinaReviewApiPassword(event.target.value)} placeholder={chinaReviewApi.passwordConfigured?"留空则保持原密码":"请输入接口密码"}/><small>{chinaReviewApi.passwordConfigured?"密码已配置；填写新值会立即替换。":"尚未配置密码，启用前必须填写。"}</small></label>
          <label><span>内部截止时间（秒）</span><input type="number" min={1} max={29} value={chinaReviewApi.deadlineMs/1000} onChange={(event)=>setChinaReviewApi((current)=>({...current,deadlineMs:Math.round(Number(event.target.value)*1000)}))}/></label>
          <label><span>默认页数</span><input type="number" min={3} max={30} value={chinaReviewApi.defaultSlideCount} onChange={(event)=>setChinaReviewApi((current)=>({...current,defaultSlideCount:Number(event.target.value)}))}/></label>
          <label><span>最大页数</span><input type="number" min={3} max={30} value={chinaReviewApi.maxSlideCount} onChange={(event)=>setChinaReviewApi((current)=>({...current,maxSlideCount:Number(event.target.value)}))}/></label>
          <label><span>国内公开模型名</span><input value={chinaReviewApi.publicModelName} maxLength={80} onChange={(event)=>setChinaReviewApi((current)=>({...current,publicModelName:event.target.value}))}/></label>
          <label className="wide"><span>预留文本模型配置</span><select value={chinaReviewApi.sourceModelId} onChange={(event)=>setChinaReviewApi((current)=>({...current,sourceModelId:event.target.value}))}><option value="">不引用模型</option>{textModels.map(({model})=><option key={model.id} value={model.id}>{model.name||model.model||model.id}</option>)}</select><small>首次默认复制现有大纲模型 ID，方便后续切换；当前离线策略不会使用该连接或 API Key。</small></label>
          <label className="wide"><span>统一拒绝话术</span><textarea value={chinaReviewApi.rejectionAnswer} maxLength={500} onChange={(event)=>setChinaReviewApi((current)=>({...current,rejectionAnswer:event.target.value}))}/></label>
          <label className="wide"><span>业务追加敏感词</span><textarea value={chinaReviewApi.additionalSensitiveWords.join("\n")} onChange={(event)=>setChinaReviewApi((current)=>({...current,additionalSensitiveWords:event.target.value.split(/\r?\n/u).map((word)=>word.trim()).filter(Boolean)}))} placeholder="每行一个敏感词"/><small>基础词库已内置 7,574 条；这里只填写 PPT 业务需要额外补充的词。</small></label>
        </div>
        <p className="china-review-api-note">规范要求 30 秒内响应。默认 25 秒截止，但当前路径没有网络等待，常规处理应在毫秒级完成；若截止时间不足，会返回预先生成的 4 页保底大纲。</p>
        {chinaReviewApiStatus&&<p className="admin-model-status admin-model-save-status" role="status" aria-live="polite">{chinaReviewApiStatus}</p>}
      </section>
      <section className="settings-card upload-limit-settings-card">
        <div className="admin-model-config-head">
          <div><h2>生成 PPT 资料上传</h2><p>限制生成入口全部资料解码后的合计大小；美化现有 PPT 不受此配置影响。</p></div>
          <Button onClick={()=>saveUploadLimits().catch((error)=>setUploadLimitsStatus(error instanceof Error?error.message:"保存失败"))}>保存上传限制</Button>
        </div>
        <div className="upload-limit-fields">
          <label><span>非会员上限</span><div><input type="number" min={1} max={100} step={1} value={uploadLimits.nonMemberMaxTotalMb} onChange={(event)=>{setUploadLimits((current)=>({...current,nonMemberMaxTotalMb:Number(event.target.value)}));setUploadLimitsStatus("");}}/><small>MB</small></div></label>
          <label><span>会员上限</span><div><input type="number" min={1} max={100} step={1} value={uploadLimits.memberMaxTotalMb} onChange={(event)=>{setUploadLimits((current)=>({...current,memberMaxTotalMb:Number(event.target.value)}));setUploadLimitsStatus("");}}/><small>MB</small></div></label>
        </div>
        <p className="image2-resolution-note">当前服务安全上限为 100 MB；最多 10 个附件。配置结构已预留按会员套餐单独覆盖。</p>
        {uploadLimitsStatus&&<p className="admin-model-status">{uploadLimitsStatus}</p>}
      </section>
      <section className="settings-card storage-quota-settings-card">
        <div className="admin-model-config-head">
          <div><h2>用户永久存储空间</h2><p>统计上传资料、项目文件和全部未删除生成版本；任务运行过程中即使超限也会继续完成。</p></div>
          <Button onClick={()=>saveStorageQuota().catch((error)=>setStorageQuotaStatus(error instanceof Error?error.message:"保存失败"))}>保存空间策略</Button>
        </div>
        <div className="upload-limit-fields">
          <label><span>普通用户额度</span><div><input type="number" min={1} max={1000000} step={1} value={storageQuota.nonMemberQuotaMb} onChange={(event)=>{setStorageQuota((current)=>({...current,nonMemberQuotaMb:Number(event.target.value)}));setStorageQuotaStatus("");}}/><small>MiB</small></div></label>
          <label><span>会员用户额度</span><div><input type="number" min={1} max={1000000} step={1} value={storageQuota.memberQuotaMb} onChange={(event)=>{setStorageQuota((current)=>({...current,memberQuotaMb:Number(event.target.value)}));setStorageQuotaStatus("");}}/><small>MiB</small></div></label>
        </div>
        <label className="admin-model-enabled storage-quota-enforcement"><input type="checkbox" checked={storageQuota.enforceLimit} onChange={(event)=>{setStorageQuota((current)=>({...current,enforceLimit:event.target.checked}));setStorageQuotaStatus("");}}/><span>限制超限用户发起新的内容生成任务</span></label>
        <p className="image2-resolution-note">关闭时仅展示超限提醒，用户仍可继续使用；开启后只拦截下一次新任务，不中断已经接收的任务，也不影响删除、下载和取消。</p>
        {storageQuotaStatus&&<p className="admin-model-status">{storageQuotaStatus}</p>}
      </section>
      <section className="settings-card image2-resolution-card">
        <div className="admin-model-config-head">
          <div><h2>Image2 PPT 分辨率</h2><p>正式任务与内部预览采用固定规格，避免后台配置漂移导致耗时和图片体积失控。</p></div>
          <Button disabled={!imageModeEnabled} onClick={()=>saveImage2Settings().catch((error)=>setImage2Status(error instanceof Error?error.message:"保存失败"))}>保存分辨率</Button>
        </div>
        <div className="image2-resolution-grid">
          {image2Presets.map((preset)=><label key={preset.value} className={image2Settings.resolution===preset.value?"selected":""}>
            <input type="radio" name="image2-resolution" value={preset.value} checked={image2Settings.resolution===preset.value} onChange={()=>{setImage2Settings({resolution:preset.value});setImage2Status("");}}/>
            <span><b>{preset.label}</b><strong>{preset.value.replace("x"," × ")}</strong><small>{preset.description}</small></span>
          </label>)}
        </div>
        <p className="image2-resolution-note">正式生成固定 1672 × 941；内部预览或开发环境固定 1280 × 720。图片模型服务可能按自身能力对请求尺寸做轻微取整。</p>
        {image2Status&&<p className="admin-model-status">{image2Status}</p>}
      </section>
      <section className="settings-card editable-export-settings-card">
        <div className="admin-model-config-head">
          <div><h2>大师 AI 可编辑 PPTX</h2><p>控制独立 Chromium 导出 Worker 的并发、任务超时与空闲资源回收。</p></div>
          <Button onClick={()=>saveEditableExportSettings().catch((error)=>setEditableExportStatus(error instanceof Error?error.message:"保存失败"))}>保存导出配置</Button>
        </div>
        <label className="admin-model-enabled"><input type="checkbox" checked={editableExportSettings.enabled} onChange={(event)=>setEditableExportSettings((current)=>({...current,enabled:event.target.checked}))}/><span>启用可编辑 PPTX 导出</span></label>
        <div className="admin-model-fields">
          <label><span>最大并发</span><input type="number" min={1} max={4} value={editableExportSettings.concurrency} onChange={(event)=>setEditableExportSettings((current)=>({...current,concurrency:Number(event.target.value)}))}/></label>
          <label><span>浏览器空闲回收（秒）</span><input type="number" min={10} max={3600} value={Math.round(editableExportSettings.browserIdleMs/1000)} onChange={(event)=>setEditableExportSettings((current)=>({...current,browserIdleMs:Number(event.target.value)*1000}))}/></label>
          <label><span>单任务超时（秒）</span><input type="number" min={30} max={1800} value={Math.round(editableExportSettings.taskTimeoutMs/1000)} onChange={(event)=>setEditableExportSettings((current)=>({...current,taskTimeoutMs:Number(event.target.value)*1000}))}/></label>
          <label><span>失败尝试次数</span><input type="number" min={1} max={3} value={editableExportSettings.maxAttempts} onChange={(event)=>setEditableExportSettings((current)=>({...current,maxAttempts:Number(event.target.value)}))}/></label>
          <label><span>最大页数</span><input type="number" min={1} max={200} value={editableExportSettings.maxPages} onChange={(event)=>setEditableExportSettings((current)=>({...current,maxPages:Number(event.target.value)}))}/></label>
        </div>
        <p className="image2-resolution-note">降低并发不会中断正在导出的任务；空闲回收只关闭 Chromium，轻量 Worker 进程会继续等待新任务。</p>
        {editableExportStatus&&<p className="admin-model-status">{editableExportStatus}</p>}
      </section>
      <section className="settings-card ocr-health-card">
        <div className="admin-model-config-head">
          <div><h2>OCR 识别服务</h2><p>发送一张系统生成的测试图，走正式识别接口；可发现进程假活、队列阻塞、超时和返回格式异常。</p></div>
          <Button onClick={()=>void testOcr()} disabled={ocrTesting||!ocrEditionEnabled}>{ocrTesting?"检测中…":"检测 OCR"}</Button>
        </div>
        <div className="admin-model-summary">
          <div><span>配置状态</span><strong>{ocrStatus?.enabled?(ocrStatus.configured?"已启用":"配置不完整"):"未启用"}</strong></div>
          <div><span>任务并发</span><strong>{ocrStatus?.concurrency??"–"}</strong></div>
          <div><span>请求超时</span><strong>{ocrStatus?`${Math.round(ocrStatus.timeoutMs/1000)} 秒`:"–"}</strong></div>
        </div>
        <dl className="ocr-health-details"><div><dt>服务地址</dt><dd>{ocrStatus?.endpoint||"–"}</dd></div></dl>
        <div className={`admin-model-test-result ${ocrTest?(ocrTest.ok?"ok":"error"):""}`}>
          {!ocrTest?"尚未执行功能检测。配置已启用不代表服务当前可用。":<><strong>{ocrTest.ok?"可用":"不可用"}</strong> · {ocrTest.message} · {ocrTest.latencyMs} ms{ocrTest.model?` · ${ocrTest.model}`:""}<br/><small>{formatDate(ocrTest.checkedAt)}</small></>}
        </div>
      </section>
      <section className="settings-card admin-model-config-card">
        <div className="admin-model-config-head">
          <div><h2>大模型配置</h2><p>{modelConfigReadOnly?"开发模式仅使用 .env.development。修改文件后请重启 API 和 Worker；这里不能编辑或保存。":"Base URL 只填基础地址，例如 http://192.168.6.147:8787/v1；文本和图片模型会自动拼接接口 path。"}</p></div>
          {!modelConfigReadOnly&&<Button onClick={()=>void saveModelPool()} disabled={modelSaving}>{modelSaving?"保存中…":"保存模型池"}</Button>}
        </div>
        {modelConfigReadOnly&&<p className="admin-model-status admin-model-save-status" role="status">当前来源：.env.development（只读）</p>}
        {modelStatus&&<p className={`admin-model-status admin-model-save-status ${modelStatusTone}`} role={modelStatusTone==="error"?"alert":"status"} aria-live="polite">{modelStatus}</p>}
        <div className="admin-model-summary">
          <div><span>文本大模型</span><strong>{textModels.filter((item)=>item.model.enabled).length}</strong></div>
          <div><span>生成图片大模型</span><strong>{imageModels.filter((item)=>item.model.enabled).length}</strong></div>
          <div><span>默认路由</span><strong>大纲 {modelNameById(modelPool,modelPool.defaults.outline)} / 图片识别 {modelPool.defaults.sourceImageAnalysis?modelNameById(modelPool,modelPool.defaults.sourceImageAnalysis):"跟随大纲"} / 对话检测 {modelNameById(modelPool,modelPool.defaults.dialogDetection)} / 图片生成 {modelNameById(modelPool,modelPool.defaults.beautify)}</strong></div>
        </div>
        {modelConfigReadOnly?<>
          <ReadonlyModelPoolSection title="文本大模型" models={textModels} tests={modelTests} onTest={testModel}/>
          <ReadonlyModelPoolSection title="生成图片大模型" models={imageModels} tests={modelTests} onTest={testModel}/>
        </>:<><ModelPoolSection
          title="文本大模型池"
          description="先为三个文本用途选择模型，再维护下方模型连接。名称只用于后台备注；Model 才是服务商要求的真实模型标识。"
          controls={<div className="admin-model-route-panel">
            <div className="admin-model-route-panel-head"><strong>文本模型用途路由</strong><span>专用模型失败时，最多回退一次默认文本模型</span></div>
            <div className="admin-model-route-grid">
              <label className="admin-model-field">
                <span>默认文本模型</span>
                <select
                  value={modelPool.defaults.outline||""}
                  disabled={!textModels.some((item)=>item.model.enabled)}
                  onChange={(event)=>setModelPool((current)=>({
                    ...current,
                    defaults:{
                      ...current.defaults,
                      outline:event.target.value,
                      singleSlide:event.target.value,
                      slideRevision:event.target.value,
                    },
                  }))}
                >
                  {textModels.filter((item)=>item.model.enabled).map(({model})=>
                    <option key={model.id} value={model.id}>{model.name||model.model||model.id}</option>
                  )}
                </select>
                <small>大纲、单页新增、页面改写和文本 Agent</small>
              </label>
              <label className="admin-model-field">
                <span>资料图片识别模型</span>
                <select
                  value={modelPool.defaults.sourceImageAnalysis||""}
                  onChange={(event)=>setModelPool((current)=>({
                    ...current,
                    defaults:{...current.defaults,sourceImageAnalysis:event.target.value||undefined},
                  }))}
                >
                  <option value="">跟随默认文本模型</option>
                  {textModels.filter((item)=>item.model.enabled&&item.model.supportsVision).map(({model})=>
                    <option key={model.id} value={model.id}>{model.name||model.model||model.id}</option>
                  )}
                </select>
                <small>仅显示已开启图片理解的文本模型</small>
              </label>
              <label className="admin-model-field">
                <span>对话检测模型</span>
                <select
                  value={modelPool.defaults.dialogDetection||""}
                  disabled={!textModels.some((item)=>item.model.enabled)}
                  onChange={(event)=>setModelPool((current)=>({
                    ...current,
                    defaults:{...current.defaults,dialogDetection:event.target.value},
                  }))}
                >
                  {textModels.filter((item)=>item.model.enabled).map(({model})=>
                    <option key={model.id} value={model.id}>{model.name||model.model||model.id}</option>
                  )}
                </select>
                <small>高级美化单页修改要求的意图检测</small>
              </label>
            </div>
          </div>}
          models={textModels}
          tests={modelTests}
          capability="text"
          onAdd={()=>addModel("text")}
          onRemove={removeModel}
          onTest={testModel}
          onUpdate={updateModel}
        />
        <ModelPoolSection
          title="生成图片大模型池"
          description="用于 image2 / 图片美化 / 视觉资产生成。启用的第一个图片模型会作为默认图片生成模型。"
          models={imageModels}
          tests={modelTests}
          capability="image"
          onAdd={()=>addModel("image")}
          onRemove={removeModel}
          onTest={testModel}
          onUpdate={updateModel}
        />
        <ModelRouteEditor pool={modelPool} onChange={(routes)=>setModelPool((current)=>({...current,routes}))}/>
        </>}
      </section>
      <ModelHealthOverview
        pool={modelPool}
        health={modelHealth}
        statusMessage={modelHealthStatus}
        recoveringModelKey={recoveringModelKey}
        onRecover={recoverModel}
        onRefresh={()=>void loadModelHealth().catch(()=>setModelHealthStatus("模型状态刷新失败，请稍后重试。"))}
      />
      <ModelHealthEventLog
        pool={modelPool}
        events={modelHealthEvents}
        category={modelHealthCategory}
        modelId={modelHealthModelId}
        onCategoryChange={(value)=>{setModelHealthCategory(value);setModelHealthModelId("");}}
        onModelChange={setModelHealthModelId}
      />
      <section className="settings-card"><h2>Feature Policy</h2><pre>{JSON.stringify(policy,null,2)}</pre></section>
      <section className="settings-card"><h2>风格目录</h2><dl><div><dt>目录版本</dt><dd>{String(catalog?.revision||"–")}</dd></div><div><dt>默认风格</dt><dd>{String(catalog?.defaultStyleCode||"–")}</dd></div><div><dt>已配置风格</dt><dd>{styles.length}</dd></div><div><dt>公开 / Tester</dt><dd>{styles.filter((style)=>style.releaseStage==="public").length} / {styles.filter((style)=>style.releaseStage==="tester").length}</dd></div></dl><div className="admin-style-list">{styles.map((style)=>{const contract=style.contract as Record<string,Record<string,string>>|undefined;return <span key={String(style.code)}><i style={{background:String(contract?.colors?.accent||"#2856e8")}}/>{String(style.name)} <small>v{String(style.version)}</small><Badge tone={style.releaseStage==="public"?"success":"warning"}>{String(style.releaseStage)}</Badge></span>;})}</div></section>
      <section className="settings-card"><h2>安全边界</h2><ul><li>模型密钥不返回浏览器</li><li>用户请求不能绕过功能策略</li><li>未上架风格由服务端拒绝</li><li>风格确认生成新 DeckVersion，不覆盖大纲</li><li>配置写操作需要 super_admin 并进入审计</li></ul></section>
    </div>
  </>;
}

function formatDate(value:unknown){if(!value)return"–";const date=new Date(String(value));return Number.isNaN(date.valueOf())?String(value):date.toLocaleString("zh-CN");}

function ReadonlyModelPoolSection({title,models,tests,onTest}:{
  title:string;
  models:Array<{model:PptModelPoolModel;index:number}>;
  tests:Record<string,ModelTestResult>;
  onTest:(index:number)=>void;
}){
  return <section className="admin-model-section">
    <h3>{title}</h3>
    {models.map(({model,index})=>{
      const test=tests[model.id];
      return <div className="admin-model-card" key={model.id}>
        <div className="admin-model-card-head"><strong>{model.name||model.model||model.id}</strong><Button onClick={()=>void onTest(index)}>测试连接</Button></div>
        <dl><div><dt>模型 ID</dt><dd>{model.model}</dd></div><div><dt>服务地址</dt><dd>{model.baseUrl}</dd></div></dl>
        {test&&<p className={`admin-model-test-result ${test.ok?"ok":"error"}`}>{test.ok?"可用":"不可用"}{test.message?` · ${test.message}`:""}{test.latencyMs!==undefined?` · ${test.latencyMs} ms`:""}</p>}
      </div>;
    })}
  </section>;
}

type PptModelCapability="text"|"image";

interface PptModelPoolModel{
  id:string;
  name:string;
  provider:"openai-compatible";
  capability:PptModelCapability;
  baseUrl:string;
  apiKey:string;
  model:string;
  maxTokens?:number;
  contextWindowTokens?:number;
  responseFormat?:"auto"|"json_schema"|"json_object"|"text";
  outlineSkill?:{
    mode:"explicit"|"unsupported";
    skillId?:string;
    version?:number;
    captureTemplateCandidates?:boolean;
  };
  concurrency?:number;
  supportsVision?:boolean;
  imageInputPolicy?:{
    maxReferenceImages?:number;
    maxImageBytes?:number;
    maxTotalBytes?:number;
    maxPixelsPerImage?:number;
    acceptedMimeTypes?:string[];
    multipartImageField?:"image"|"image[]";
    defaultPackingMode?:"adaptive_independent"|"independent_original"|"independent_resized"|"contact_sheet";
    ordinaryReferenceMaxEdge?:number;
    primaryReferenceMaxEdge?:number;
    protectedReferenceMaxEdge?:number;
  };
  enabled:boolean;
}

interface PptModelPool{
  schemaVersion?:"model-pool/v2";
  revision?:number;
  models:PptModelPoolModel[];
  defaults:{
    outline?:string;
    sourceImageAnalysis?:string;
    singleSlide?:string;
    slideRevision?:string;
    dialogDetection?:string;
    beautify?:string;
  };
  routes?:Partial<Record<ModelRouteCategory,{candidates:string[]}>>;
}

function userStatusLabel(status:string){return ({active:"正常",disabled:"已停用",closing:"注销中",closed:"已注销"} as Record<string,string>)[status]||status;}
function AdminClosureConfirmDialog({user,busy,onClose,onConfirm}:{user:AdminUser;busy:boolean;onClose:()=>void;onConfirm:()=>void}){return <div className="payment-overlay" role="dialog" aria-modal="true" aria-labelledby="admin-closure-title"><section className="password-dialog"><button className="payment-close" type="button" aria-label="关闭" disabled={busy} onClick={onClose}>×</button><Badge tone="danger">不可恢复</Badge><h2 id="admin-closure-title">确认正式注销账户？</h2><p>将正式注销 {user.phone||user.nickname}。该操作会清除其密码并保持禁止登录、注册。</p><Button variant="danger" disabled={busy} onClick={onConfirm}>{busy?"正在确认…":"确认注销"}</Button><button className="dialog-skip" type="button" disabled={busy} onClick={onClose}>取消</button></section></div>;}

type ModelRouteCategory="text"|"vision"|"dialog_detection"|"image_generation";

interface ModelTestResult{
  ok:boolean;
  message?:string;
  status?:number;
  latencyMs?:number;
  modelFound?:boolean;
  outlineSkill?:PptModelPoolModel["outlineSkill"];
  skillProvisionAction?:"verified"|"discovered"|"uploaded";
}

interface OcrServiceConfiguration{
  enabled:boolean;
  configured:boolean;
  endpoint:string;
  timeoutMs:number;
  concurrency:number;
}

interface OcrServiceTestResult{
  ok:boolean;
  status:"healthy"|"unhealthy"|"disabled"|"misconfigured";
  message:string;
  latencyMs:number;
  checkedAt:string;
  model?:string;
  language?:string;
  recognizedText?:boolean;
}

interface EditionAdminInfo{
  edition:{
    edition:"global"|"china";
    revision:string;
    capabilities:Record<string,boolean>;
  };
  deploymentId:string;
}

interface Image2Settings{
  resolution:string;
}

interface EditableExportSettings{
  enabled:boolean;
  concurrency:number;
  browserIdleMs:number;
  taskTimeoutMs:number;
  maxAttempts:number;
  maxPages:number;
}

interface PptGenerationUploadLimits{
  revision:number;
  nonMemberMaxTotalMb:number;
  memberMaxTotalMb:number;
  planOverridesMb:Record<string,number>;
}

interface StorageQuotaPolicy{
  revision:number;
  nonMemberQuotaMb:number;
  memberQuotaMb:number;
  planOverridesMb:Record<string,number>;
  enforceLimit:boolean;
}

interface ChinaReviewApiAdminSettings{
  schemaVersion:"china-review-api/v1";
  enabled:boolean;
  strategy:"offline_template";
  username:string;
  passwordConfigured:boolean;
  deadlineMs:number;
  defaultSlideCount:number;
  maxSlideCount:number;
  rejectionAnswer:string;
  publicModelName:string;
  sourceModelId:string;
  additionalSensitiveWords:string[];
  endpoint:"/test";
}

function defaultChinaReviewApiSettings():ChinaReviewApiAdminSettings{
  return {
    schemaVersion:"china-review-api/v1",enabled:false,strategy:"offline_template",
    username:"",passwordConfigured:false,
    deadlineMs:25000,defaultSlideCount:10,maxSlideCount:20,
    rejectionAnswer:"当前请求暂不支持。仅支持合规的 PPT 制作类需求。",
    publicModelName:"DeepSeek",sourceModelId:"",additionalSensitiveWords:[],
    endpoint:"/test",
  };
}

function defaultPptGenerationUploadLimits():PptGenerationUploadLimits{
  return {revision:1,nonMemberMaxTotalMb:100,memberMaxTotalMb:100,planOverridesMb:{}};
}

function defaultStorageQuotaPolicy():StorageQuotaPolicy{
  return {revision:1,nonMemberQuotaMb:200,memberQuotaMb:200,planOverridesMb:{},enforceLimit:false};
}

function defaultEditableExportSettings():EditableExportSettings{
  return {enabled:true,concurrency:2,browserIdleMs:180000,taskTimeoutMs:300000,maxAttempts:2,maxPages:100};
}

interface Image2ResolutionPreset{
  value:string;
  label:string;
  description:string;
}

function defaultImage2Presets():Image2ResolutionPreset[]{
  return [
    {value:"1672x941",label:"正式生成",description:"正式 PPT 任务固定使用的单页图片分辨率"},
  ];
}

function AdminModelSwitch({
  checked,label,onChange,compact=false,
}:{
  checked:boolean;
  label:string;
  onChange:(checked:boolean)=>void;
  compact?:boolean;
}){
  return <label className={`admin-model-switch${compact?" compact":""}`}>
    <input type="checkbox" checked={checked} onChange={(event)=>onChange(event.target.checked)}/>
    <span className="admin-model-switch-track" aria-hidden="true"><i/></span>
    <span className="admin-model-switch-label">{label}</span>
  </label>;
}

function ModelPoolSection({
  title,description,controls,models,tests,capability,onAdd,onRemove,onTest,onUpdate,
}:{
  title:string;
  description:string;
  controls?:ReactNode;
  models:Array<{model:PptModelPoolModel;index:number}>;
  tests:Record<string,ModelTestResult>;
  capability:PptModelCapability;
  onAdd:()=>void;
  onRemove:(index:number)=>void;
  onTest:(index:number)=>void;
  onUpdate:(index:number,patch:Partial<PptModelPoolModel>)=>void;
}){
  return <section className="admin-model-section">
    <div className="admin-model-section-head"><h3>{title}</h3><p>{description}</p></div>
    {controls}
    <div className="admin-model-row-grid">
      {models.length?models.map(({model,index})=><div className="admin-model-row" key={model.id}>
        <div className="admin-model-row-head">
          <AdminModelSwitch compact checked={model.enabled} label="启用" onChange={(checked)=>onUpdate(index,{enabled:checked})}/>
          <input className="admin-model-name" value={model.name} onChange={(event)=>onUpdate(index,{name:event.target.value})} placeholder="名称（仅作备注）" title="名称仅用于后台识别，可自由填写"/>
          <Badge tone={capability==="image"?"warning":"brand"}>{capability==="image"?"生成图片模型":"文本生成模型"}</Badge>
          <Button variant="danger" onClick={()=>onRemove(index)}>删除</Button>
        </div>
        <div className="admin-model-fields">
          <label className="admin-model-field admin-model-field-wide"><span>Base URL（基础地址）</span><input value={model.baseUrl} onChange={(event)=>onUpdate(index,{baseUrl:event.target.value})} onBlur={(event)=>onUpdate(index,{baseUrl:normalizeModelBaseUrl(event.currentTarget.value)})} placeholder="http://192.168.6.147:8787/v1"/></label>
          <label className="admin-model-field"><span>Model</span><input value={model.model} onChange={(event)=>onUpdate(index,{model:event.target.value})} placeholder={capability==="image"?"gpt-image-2":"gpt-5.5"}/></label>
          <label className="admin-model-field"><span>API Key</span><input type="password" value={model.apiKey} onChange={(event)=>onUpdate(index,{apiKey:event.target.value})} placeholder="API Key"/></label>
          <label className="admin-model-field admin-model-field-small"><span>max_tokens</span><input type="number" min={1} value={model.maxTokens??""} onChange={(event)=>onUpdate(index,{maxTokens:positiveNumber(event.target.value)})}/></label>
          {model.capability==="text"?<label className="admin-model-field admin-model-field-small"><span>上下文窗口 tokens</span><input type="number" min={8192} value={model.contextWindowTokens??128000} onChange={(event)=>onUpdate(index,{contextWindowTokens:positiveNumber(event.target.value)})}/></label>:null}
          {model.capability==="text"?<label className="admin-model-field"><span>response_format</span><select value={model.responseFormat||"json_object"} onChange={(event)=>onUpdate(index,{responseFormat:event.target.value as NonNullable<PptModelPoolModel["responseFormat"]>})}><option value="json_object">json_object（兼容）</option><option value="json_schema">json_schema（严格）</option><option value="text">text（无格式约束）</option><option value="auto">auto（自动探测）</option></select></label>:null}
          {model.capability==="text"?<AdminModelSwitch checked={model.outlineSkill?.mode==="explicit"} label="启用 PPT 大纲 Skill" onChange={(checked)=>onUpdate(index,{outlineSkill:checked?{mode:"explicit",captureTemplateCandidates:model.outlineSkill?.captureTemplateCandidates!==false}:{mode:"unsupported"}})}/>:null}
          {model.capability==="text"&&model.outlineSkill?.mode==="explicit"?<>
            <div className="admin-model-field admin-model-field-wide"><span>Skill 配置</span><small>系统自动使用已验证的 Skill 与版本，无需人工填写。</small></div>
            <AdminModelSwitch checked={model.outlineSkill.captureTemplateCandidates!==false} label="沉淀候选叙事结构" onChange={(checked)=>onUpdate(index,{outlineSkill:{...model.outlineSkill!,mode:"explicit",captureTemplateCandidates:checked}})}/>
          </>:null}
          <label className="admin-model-field admin-model-field-small"><span>{capability==="image"?"并行任务数":"并发数"}</span><input type="number" min={1} max={16} step={1} value={model.concurrency??1} onChange={(event)=>onUpdate(index,{concurrency:Math.min(16,Math.max(1,Math.floor(positiveNumber(event.target.value)||1)))})}/></label>
          {capability==="text"?<AdminModelSwitch checked={model.supportsVision!==false} label="图片理解" onChange={(checked)=>onUpdate(index,{supportsVision:checked})}/>:null}
          {capability==="image"?<>
            <label className="admin-model-field"><span>参考图输入模式</span><select value={model.imageInputPolicy?.defaultPackingMode||"adaptive_independent"} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,defaultPackingMode:event.target.value as NonNullable<PptModelPoolModel["imageInputPolicy"]>["defaultPackingMode"]}})}><option value="adaptive_independent">独立输入优先（推荐）</option><option value="independent_original">原图分别输入</option><option value="independent_resized">缩放后分别输入</option><option value="contact_sheet">普通素材合并素材表</option></select></label>
            <label className="admin-model-field admin-model-field-small"><span>最多参考图</span><input type="number" min={1} max={8} value={model.imageInputPolicy?.maxReferenceImages??8} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,maxReferenceImages:positiveNumber(event.target.value)||8}})}/></label>
            <label className="admin-model-field admin-model-field-small"><span>单图上限 MB</span><input type="number" min={1} value={Math.round((model.imageInputPolicy?.maxImageBytes||10*1024*1024)/(1024*1024))} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,maxImageBytes:(positiveNumber(event.target.value)||10)*1024*1024}})}/></label>
            <label className="admin-model-field admin-model-field-small"><span>总输入上限 MB</span><input type="number" min={1} value={Math.round((model.imageInputPolicy?.maxTotalBytes||60*1024*1024)/(1024*1024))} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,maxTotalBytes:(positiveNumber(event.target.value)||60)*1024*1024}})}/></label>
            <label className="admin-model-field admin-model-field-small"><span>multipart 图片字段</span><select value={model.imageInputPolicy?.multipartImageField||"image"} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,multipartImageField:event.target.value==="image[]"?"image[]":"image"}})}><option value="image">image</option><option value="image[]">image[]</option></select></label>
            <label className="admin-model-field admin-model-field-small"><span>普通素材最长边 px</span><input type="number" min={128} max={4096} value={model.imageInputPolicy?.ordinaryReferenceMaxEdge??512} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,ordinaryReferenceMaxEdge:positiveNumber(event.target.value)||512}})}/></label>
            <label className="admin-model-field admin-model-field-small"><span>主视觉最长边 px</span><input type="number" min={128} max={4096} value={model.imageInputPolicy?.primaryReferenceMaxEdge??1024} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,primaryReferenceMaxEdge:positiveNumber(event.target.value)||1024}})}/></label>
            <label className="admin-model-field admin-model-field-small"><span>高保真最长边 px</span><input type="number" min={128} max={4096} value={model.imageInputPolicy?.protectedReferenceMaxEdge??2048} onChange={(event)=>onUpdate(index,{imageInputPolicy:{...model.imageInputPolicy,protectedReferenceMaxEdge:positiveNumber(event.target.value)||2048}})}/></label>
          </>:null}
        </div>
        <div className="admin-model-test">
          <Button variant="secondary" onClick={()=>onTest(index)}>测试连接</Button>
          <div className={`admin-model-test-result ${tests[model.id]?.ok===true?"ok":tests[model.id]?.ok===false?"error":""}`}>{formatModelTestResult(tests[model.id])}</div>
        </div>
      </div>):<div className="admin-model-empty">暂无模型，请添加配置。</div>}
    </div>
    <Button variant="secondary" onClick={onAdd}>添加{capability==="image"?"图片":"文本"}模型</Button>
  </section>;
}

type ModelRuntimeStatus="pending_probe"|"available"|"cooling"|"half_open"|"configuration_error";
interface ModelHealthSnapshot{
  category:ModelRouteCategory;
  modelId:string;
  status:ModelRuntimeStatus;
  lastFailureAt?:string;
  lastFailureKind?:string;
  lastFailureReason?:string;
  cooldownUntil?:string;
  halfOpenLeaseActive:boolean;
  lastSuccessAt?:string;
}
interface ModelHealthEvent{
  id:string;
  category:ModelRouteCategory;
  modelId?:string;
  eventType:string;
  fromStatus?:ModelRuntimeStatus;
  toStatus?:ModelRuntimeStatus;
  failureKind?:string;
  reason?:string;
  source:"automatic"|"admin";
  actor?:string;
  occurredAt:string;
}

function ModelHealthOverview({
  pool,health,statusMessage,recoveringModelKey,onRecover,onRefresh,
}:{
  pool:PptModelPool;health:ModelHealthSnapshot[]|undefined;statusMessage:string;recoveringModelKey:string;
  onRecover:(category:ModelRouteCategory,modelId:string)=>void;onRefresh:()=>void;
}){
  const categories:Array<{value:ModelRouteCategory;label:string}>=[
    {value:"text",label:"文本大模型"},{value:"vision",label:"资料图片识别"},
    {value:"dialog_detection",label:"对话检测"},{value:"image_generation",label:"生成图片"},
  ];
  return <section className="settings-card admin-model-health-card">
    <div className="admin-model-config-head"><div><h2>大模型运行状态</h2><p>状态按“类别 + 模型”独立记录。保存配置或人工恢复后会进入待试用，只有下一次真实请求成功才恢复可用。</p></div><Button variant="secondary" onClick={onRefresh}>刷新状态</Button></div>
    {statusMessage?<p className="admin-model-status admin-model-save-status" role="status">{statusMessage}</p>:null}
    {!health? <LoadingBlock/> : <div className="admin-model-health-grid">
      {categories.map(({value,label})=>{
        const candidates=pool.routes?.[value]?.candidates||[];
        return <article className="admin-model-health-group" key={value}><header><strong>{label}</strong><small>{candidates.length} 个候选模型</small></header>
          {candidates.length?candidates.map((modelId)=>{
            const model=pool.models.find((item)=>item.id===modelId);
            const snapshot=health.find((item)=>item.category===value&&item.modelId===modelId);
            const status=snapshot?.status||"pending_probe";
            const key=`${value}:${modelId}`;
            return <div className="admin-model-health-row" key={modelId}>
              <div><strong>{model?.name||model?.model||modelId}</strong><small>{model?.model||modelId}</small></div>
              <ModelHealthBadge status={status}/>
              <div className="admin-model-health-detail">
                {status==="cooling"&&snapshot?.cooldownUntil?<span>冷却至 {formatDate(snapshot.cooldownUntil)}</span>:null}
                {snapshot?.lastFailureReason?<span title={snapshot.lastFailureReason}>最近失败：{snapshot.lastFailureReason}</span>:null}
                {!snapshot?.lastFailureReason&&status==="available"?<span>最近成功：{snapshot?.lastSuccessAt?formatDate(snapshot.lastSuccessAt):"–"}</span>:null}
                {status==="pending_probe"?<span>等待下一次真实请求试用</span>:null}
                {status==="half_open"?<span>正在由一个请求独占试用</span>:null}
              </div>
              <Button variant="secondary" onClick={()=>onRecover(value,modelId)} disabled={recoveringModelKey===key}>{recoveringModelKey===key?"恢复中…":"立即恢复"}</Button>
            </div>;
          }):<EmptyState title="该类别尚未配置候选模型" description="请在上方大模型配置中添加并排序候选模型。"/>}
        </article>;
      })}
    </div>}
  </section>;
}

function ModelHealthEventLog({
  pool,events,category,modelId,onCategoryChange,onModelChange,
}:{
  pool:PptModelPool;events:ModelHealthEvent[]|undefined;category:""|ModelRouteCategory;modelId:string;
  onCategoryChange:(value:""|ModelRouteCategory)=>void;onModelChange:(value:string)=>void;
}){
  const models=pool.models.filter((model)=>!category||(pool.routes?.[category]?.candidates||[]).includes(model.id));
  const visible=(events||[]).filter((event)=>(!category||event.category===category)&&(!modelId||event.modelId===modelId));
  return <section className="settings-card admin-model-health-log-card">
    <div className="admin-model-config-head"><div><h2>大模型状态日志</h2><p>显示最近 500 条状态变更事件，可按类别和模型筛选。</p></div><strong>{visible.length} / {events?.length||0} 条</strong></div>
    <div className="admin-model-health-filters">
      <label><span>类别</span><select value={category} onChange={(event)=>onCategoryChange(event.target.value as ""|ModelRouteCategory)}><option value="">全部类别</option><option value="text">文本大模型</option><option value="vision">资料图片识别</option><option value="dialog_detection">对话检测</option><option value="image_generation">生成图片</option></select></label>
      <label><span>模型</span><select value={modelId} onChange={(event)=>onModelChange(event.target.value)}><option value="">全部模型</option>{models.map((model)=><option key={model.id} value={model.id}>{model.name||model.model||model.id}</option>)}</select></label>
    </div>
    {!events?<LoadingBlock/>:visible.length?<div className="admin-model-health-events" role="list">{visible.map((event)=><article key={event.id} role="listitem"><i aria-hidden="true">{event.source==="admin"?"管":"自"}</i><div><header><strong>{modelHealthEventLabel(event.eventType)}</strong><ModelHealthBadge status={event.toStatus||"pending_probe"}/><time>{formatDate(event.occurredAt)}</time></header><p>{modelHealthCategoryLabel(event.category)}{event.modelId?` · ${pool.models.find((model)=>model.id===event.modelId)?.name||event.modelId}`:""}{event.reason?` · ${event.reason}`:""}</p><small>{event.failureKind?`失败类型：${event.failureKind} · `:""}{event.source==="admin"?`管理员：${event.actor||"–"}`:"自动检测"}</small></div></article>)}</div>:<EmptyState title="没有匹配的状态日志" description="调整筛选条件，或等待模型调用产生新的状态事件。"/>}
  </section>;
}

function ModelHealthBadge({status}:{status:ModelRuntimeStatus}){
  const labels:Record<ModelRuntimeStatus,string>={pending_probe:"待试用",available:"可用",cooling:"冷却中",half_open:"试用中",configuration_error:"配置异常"};
  const tone:Record<ModelRuntimeStatus,"success"|"warning"|"danger"|"brand">={pending_probe:"brand",available:"success",cooling:"warning",half_open:"brand",configuration_error:"danger"};
  return <Badge tone={tone[status]}>{labels[status]}</Badge>;
}
function modelHealthCategoryLabel(category:ModelRouteCategory):string{return {text:"文本大模型",vision:"资料图片识别",dialog_detection:"对话检测",image_generation:"生成图片"}[category];}
function modelHealthEventLabel(type:string):string{return ({cooling_started:"进入冷却",half_open_started:"开始试用",probe_requested:"请求试用",recovered:"恢复可用",configuration_error:"配置异常",admin_recovered:"人工恢复",category_unavailable:"类别不可用"} as Record<string,string>)[type]||type;}

function ModelRouteEditor({pool,onChange}:{pool:PptModelPool;onChange:(routes:NonNullable<PptModelPool["routes"]>)=>void}){
  const definitions:Array<{category:ModelRouteCategory;label:string;description:string;eligible:(model:PptModelPoolModel)=>boolean}>= [
    {category:"text",label:"文本大模型",description:"大纲、文案与内容推理",eligible:(model)=>isModelRouteEligible("text",model)},
    {category:"vision",label:"资料图片识别",description:"读取上传图片与参考资料",eligible:(model)=>isModelRouteEligible("vision",model)},
    {category:"dialog_detection",label:"对话检测",description:"识别输入中的对话式需求",eligible:(model)=>isModelRouteEligible("dialog_detection",model)},
    {category:"image_generation",label:"生成图片",description:"生成页面配图与视觉素材",eligible:(model)=>isModelRouteEligible("image_generation",model)},
  ];
  const update=(category:ModelRouteCategory,candidates:string[])=>onChange({...pool.routes,[category]:{candidates}});
  const move=(category:ModelRouteCategory,candidates:string[],from:number,to:number)=>{
    if(to<0||to>=candidates.length)return;
    const next=[...candidates];
    [next[from],next[to]]=[next[to]!,next[from]!];
    update(category,next);
  };
  return <section className="admin-model-section admin-model-route-panel">
    <div className="admin-model-route-heading">
      <div><span className="admin-route-kicker">MODEL ROUTING</span><h3>类别候选路由</h3><p>按顺序执行：主模型不可用时会立即尝试下一候选；冷却一小时后，下一次真实请求会重新探测恢复的模型。</p></div>
      <span className="admin-route-heading-note">顺序决定优先级</span>
    </div>
    <div className="admin-route-card-grid">
    {definitions.map(({category,label,description,eligible})=>{
      const candidates=pool.routes?.[category]?.candidates||[];
      const selectable=pool.models.filter((model)=>model.enabled&&eligible(model)&&!candidates.includes(model.id));
      return <article className="admin-route-card" key={category}>
        <header className="admin-route-card-head"><div><h4>{label}</h4><p>{description}</p></div><span className={`admin-route-count ${candidates.length?"is-configured":""}`}>{candidates.length?`${candidates.length} 个候选`:"待配置"}</span></header>
        <ol className="admin-route-candidate-list" aria-label={`${label}候选模型，按优先级排序`}>
          {candidates.length?candidates.map((id,index)=>{
            const model=pool.models.find((item)=>item.id===id);
            const name=model?.name||model?.model||id;
            return <li className="admin-route-candidate" key={id}>
              <span className="admin-route-priority">{index===0?"主":"备"}<b>{String(index+1).padStart(2,"0")}</b></span>
              <span className="admin-route-model"><strong title={name}>{name}</strong><small title={model?.model||id}>{model?.model||id}</small></span>
              <span className="admin-route-actions">
                <Button type="button" className="admin-route-icon-button" variant="ghost" aria-label={`将 ${name} 上移`} title="上移" onClick={()=>move(category,candidates,index,index-1)} disabled={index===0}>↑</Button>
                <Button type="button" className="admin-route-icon-button" variant="ghost" aria-label={`将 ${name} 下移`} title="下移" onClick={()=>move(category,candidates,index,index+1)} disabled={index===candidates.length-1}>↓</Button>
                <Button type="button" className="admin-route-remove-button" variant="ghost" aria-label={`从 ${label} 路由移除 ${name}`} title="移除" onClick={()=>update(category,candidates.filter((item)=>item!==id))}>×</Button>
              </span>
            </li>;
          }):<li className="admin-route-empty"><span>尚未添加候选模型</span><small>请从下方选择可用模型。</small></li>}
        </ol>
        <label className="admin-route-add"><span>添加候选</span><select value="" aria-label={`为 ${label} 添加候选模型`} onChange={(event)=>{const id=event.target.value;if(id)update(category,[...candidates,id]);}} disabled={!selectable.length}><option value="">{selectable.length?"选择模型加入队列…":"没有可添加的模型"}</option>{selectable.map((model)=><option key={model.id} value={model.id}>{model.name||model.model||model.id}</option>)}</select></label>
      </article>;
    })}
    </div>
  </section>;
}

function normalizeModelPool(pool:unknown):PptModelPool{
  const source=pool&&typeof pool==="object"?pool as Partial<PptModelPool>:defaultModelPool();
  const rawModels=Array.isArray(source.models)?source.models:[];
  const models=rawModels.map(normalizeModel);
  const normalizedModels=models.length?models:defaultModelPool().models;
  const defaults=buildModelDefaults(normalizedModels,source.defaults||{});
  const candidates=(category:ModelRouteCategory,eligible:(model:PptModelPoolModel)=>boolean,legacy:string[])=>{
    const configured=source.routes?.[category]?.candidates;
    const ids=Array.isArray(configured)?configured.map(String):legacy;
    return [...new Set(ids)].filter((id)=>normalizedModels.some((model)=>model.id===id&&eligible(model)));
  };
  return {
    schemaVersion:"model-pool/v2",revision:Math.max(1,Number(source.revision)||1),models:normalizedModels,defaults,
    routes:{
      text:{candidates:candidates("text",(model)=>isModelRouteEligible("text",model),[defaults.outline||""])},
      vision:{candidates:candidates("vision",(model)=>isModelRouteEligible("vision",model),[defaults.sourceImageAnalysis||defaults.outline||""])},
      dialog_detection:{candidates:candidates("dialog_detection",(model)=>isModelRouteEligible("dialog_detection",model),[defaults.dialogDetection||defaults.outline||""])},
      image_generation:{candidates:candidates("image_generation",(model)=>isModelRouteEligible("image_generation",model),[defaults.beautify||""])},
    },
  };
}

function isModelRouteEligible(category:ModelRouteCategory,model:PptModelPoolModel):boolean{
  if(!model.enabled||!model.baseUrl.trim()||!model.apiKey.trim()||!model.model.trim())return false;
  if(category==="image_generation")return model.capability==="image";
  return model.capability==="text"&&(category!=="vision"||model.supportsVision!==false);
}

function normalizeModel(model:unknown,index:number):PptModelPoolModel{
  const source=model&&typeof model==="object"?model as Partial<PptModelPoolModel>&{max_tokens?:number}:{};
  const capability:PptModelCapability=source.capability==="image"?"image":"text";
  const maxTokens=positiveNumber(source.maxTokens||source.max_tokens);
  const contextWindowTokens=positiveNumber(source.contextWindowTokens);
  return {
    id:String(source.id||createModelId(capability,index)),
    name:String(source.name||(capability==="image"?"生成图片大模型":"文本大模型")),
    provider:"openai-compatible",
    capability,
    baseUrl:normalizeModelBaseUrl(String(source.baseUrl||"")),
    apiKey:String(source.apiKey||""),
    model:String(source.model||""),
    ...(maxTokens?{maxTokens}:{}),
    ...(capability==="text"?{contextWindowTokens:contextWindowTokens||128000,responseFormat:source.responseFormat||"json_object",outlineSkill:normalizeOutlineSkill(source.outlineSkill)}:{}),
    concurrency:Math.min(16,Math.max(1,Math.floor(positiveNumber(source.concurrency)||1))),
    ...(capability==="text"?{supportsVision:source.supportsVision!==false}:{}),
    ...(capability==="image"?{imageInputPolicy:normalizeImageInputPolicy(source.imageInputPolicy)}:{}),
    enabled:source.enabled!==false,
  };
}

function buildModelDefaults(models:PptModelPoolModel[],previous:PptModelPool["defaults"]={}):PptModelPool["defaults"]{
  const enabledText=models.find((model)=>model.enabled&&model.capability==="text")?.id;
  const enabledImage=models.find((model)=>model.enabled&&model.capability==="image")?.id;
  const firstText=enabledText||models.find((model)=>model.capability==="text")?.id||"";
  const firstImage=enabledImage||models.find((model)=>model.capability==="image")?.id||firstText;
  const keep=(key:keyof PptModelPool["defaults"],capability:PptModelCapability,fallback:string)=>{
    const previousId=previous[key];
    return models.some((model)=>model.id===previousId&&model.enabled&&model.capability===capability)?previousId:fallback;
  };
  const sourceImageAnalysis=models.some((model)=>
    model.id===previous.sourceImageAnalysis&&model.enabled&&model.capability==="text"&&model.supportsVision,
  )?previous.sourceImageAnalysis:undefined;
  return {
    outline:keep("outline","text",firstText),
    ...(sourceImageAnalysis?{sourceImageAnalysis}:{}),
    singleSlide:keep("singleSlide","text",firstText),
    slideRevision:keep("slideRevision","text",firstText),
    dialogDetection:keep("dialogDetection","text",firstText),
    beautify:keep("beautify","image",firstImage),
  };
}

function defaultModelPool():PptModelPool{
  const models:PptModelPoolModel[]=[
    {id:"text-main",name:"文本大模型",provider:"openai-compatible",capability:"text",baseUrl:"",apiKey:"",model:"",maxTokens:4096,contextWindowTokens:128000,responseFormat:"json_object",outlineSkill:{mode:"unsupported"},concurrency:1,supportsVision:true,enabled:true},
    {id:"image-main",name:"生成图片大模型",provider:"openai-compatible",capability:"image",baseUrl:"",apiKey:"",model:"",maxTokens:1024,concurrency:1,imageInputPolicy:defaultImageInputPolicy(),enabled:true},
  ];
  return {models,defaults:{outline:"text-main",singleSlide:"text-main",slideRevision:"text-main",dialogDetection:"text-main",beautify:"image-main"}};
}

function newModel(capability:PptModelCapability):PptModelPoolModel{
  return {
    id:createModelId(capability),
    name:capability==="image"?"生成图片大模型":"文本大模型",
    provider:"openai-compatible",
    capability,
    baseUrl:"",
    apiKey:"",
    model:"",
    maxTokens:capability==="image"?1024:4096,
    ...(capability==="text"?{contextWindowTokens:128000,responseFormat:"json_object" as const,outlineSkill:{mode:"unsupported" as const}}:{}),
    concurrency:1,
    ...(capability==="text"?{supportsVision:true}:{}),
    ...(capability==="image"?{imageInputPolicy:defaultImageInputPolicy()}:{}),
    enabled:true,
  };
}

function createModelId(capability:PptModelCapability,index=0):string{
  return `${capability}-${Date.now().toString(36)}-${Math.max(index,0)}`;
}

function positiveNumber(value:unknown):number|undefined{
  const number=Number(value);
  return Number.isFinite(number)&&number>0?Math.floor(number):undefined;
}

function normalizeOutlineSkill(value:unknown):NonNullable<PptModelPoolModel["outlineSkill"]>{
  if(!value||typeof value!=="object")return {mode:"unsupported"};
  const source=value as Partial<NonNullable<PptModelPoolModel["outlineSkill"]>>;
  if(source.mode!=="explicit")return {mode:"unsupported"};
  const skillId=typeof source.skillId==="string"&&/^skill_[A-Za-z0-9]+$/.test(source.skillId)?source.skillId:undefined;
  const version=Number.isSafeInteger(source.version)&&Number(source.version)>=1?Number(source.version):undefined;
  return {
    mode:"explicit",
    ...(skillId?{skillId}:{}),
    ...(version?{version}:{}),
    captureTemplateCandidates:source.captureTemplateCandidates!==false,
  };
}

function defaultImageInputPolicy():NonNullable<PptModelPoolModel["imageInputPolicy"]>{
  return {maxReferenceImages:8,maxImageBytes:10*1024*1024,maxTotalBytes:60*1024*1024,maxPixelsPerImage:50_000_000,acceptedMimeTypes:["image/png","image/jpeg","image/webp"],multipartImageField:"image",defaultPackingMode:"adaptive_independent",ordinaryReferenceMaxEdge:512,primaryReferenceMaxEdge:1024,protectedReferenceMaxEdge:2048};
}

function normalizeImageInputPolicy(value:unknown):NonNullable<PptModelPoolModel["imageInputPolicy"]>{
  const source=value&&typeof value==="object"?value as NonNullable<PptModelPoolModel["imageInputPolicy"]>:{};
  const defaults=defaultImageInputPolicy();
  const defaultPackingMode=source.defaultPackingMode==="adaptive_independent"||source.defaultPackingMode==="contact_sheet"||source.defaultPackingMode==="independent_resized"||source.defaultPackingMode==="independent_original"?source.defaultPackingMode:"adaptive_independent";
  const acceptedMimeTypes=Array.isArray(source.acceptedMimeTypes)?source.acceptedMimeTypes.map(String).filter((item)=>["image/png","image/jpeg","image/webp"].includes(item)):[];
  return {
    maxReferenceImages:positiveNumber(source.maxReferenceImages)||defaults.maxReferenceImages,
    maxImageBytes:positiveNumber(source.maxImageBytes)||defaults.maxImageBytes,
    maxTotalBytes:positiveNumber(source.maxTotalBytes)||defaults.maxTotalBytes,
    maxPixelsPerImage:positiveNumber(source.maxPixelsPerImage)||defaults.maxPixelsPerImage,
    acceptedMimeTypes:acceptedMimeTypes.length?acceptedMimeTypes:defaults.acceptedMimeTypes,
    multipartImageField:source.multipartImageField==="image[]"?"image[]":"image",
    defaultPackingMode,
    ordinaryReferenceMaxEdge:positiveNumber(source.ordinaryReferenceMaxEdge)||defaults.ordinaryReferenceMaxEdge,
    primaryReferenceMaxEdge:positiveNumber(source.primaryReferenceMaxEdge)||defaults.primaryReferenceMaxEdge,
    protectedReferenceMaxEdge:positiveNumber(source.protectedReferenceMaxEdge)||defaults.protectedReferenceMaxEdge,
  };
}

function normalizeModelBaseUrl(value:string):string{
  const text=String(value||"").trim().replace(/^[`'"\u2018\u2019\u201C\u201D]+|[`'"\u2018\u2019\u201C\u201D]+$/g,"");
  if(!text)return"";
  try{
    const url=new URL(text);
    url.search="";
    url.hash="";
    let segments=url.pathname.split("/").filter(Boolean);
    segments=stripKnownEndpointSegments(segments);
    const versionIndex=findLastIndex(segments,(segment)=>/^v\d+$/i.test(decodePathSegment(segment)));
    if(versionIndex>=0)segments=segments.slice(0,versionIndex+1);
    url.pathname=segments.length?`/${segments.join("/")}`:"";
    return url.toString().replace(/\/+$/,"");
  }catch{
    return text
      .replace(/[?#].*$/,"")
      .replace(/\/(?:chat\/completions|images\/(?:generations|edits)|models)\/?$/i,"")
      .replace(/\/(v\d+)(?:\/.*)?$/i,"/$1")
      .replace(/\/+$/,"");
  }
}

function stripKnownEndpointSegments(segments:string[]):string[]{
  const suffixes=[["chat","completions"],["images","generations"],["images","edits"],["models"]];
  for(const suffix of suffixes){
    if(segments.length<suffix.length)continue;
    const tail=segments.slice(segments.length-suffix.length);
    if(tail.every((segment,index)=>decodePathSegment(segment).toLowerCase()===suffix[index])){
      return segments.slice(0,segments.length-suffix.length);
    }
  }
  return segments;
}

function findLastIndex<T>(items:T[],predicate:(item:T)=>boolean):number{
  for(let index=items.length-1;index>=0;index-=1){
    if(predicate(items[index]))return index;
  }
  return -1;
}

function decodePathSegment(segment:string):string{
  try{return decodeURIComponent(segment);}catch{return segment;}
}

function modelNameById(pool:PptModelPool,id?:string):string{
  const model=pool.models.find((item)=>item.id===id);
  return model?.name||model?.model||model?.id||"未配置";
}

function formatModelTestResult(result?:ModelTestResult):string{
  if(!result)return"点击测试连接后显示结果";
  const parts=[result.message||(result.ok?"测试通过":"测试失败")];
  if(typeof result.status==="number")parts.push(`HTTP ${result.status}`);
  if(typeof result.latencyMs==="number")parts.push(`${result.latencyMs}ms`);
  if(result.modelFound===false)parts.push("Model 未匹配");
  return parts.join(" · ");
}

function creditKindLabel(kind: CreditTransaction["kind"]): string {
  return ({ grant: "赠送", consume: "消耗", refund: "退款" } as const)[kind];
}

function creditReasonLabel(reason: string): string {
  return ({
    signup_gift: "注册赠送",
    admin_gift: "管理员赠送",
    membership_gift: "会员赠送",
    master_ai_generate: "大师 AI 生成",
    image2_generate: "Image2 生成",
    image2_revision: "Image2 修改",
    generation_failed: "生成失败退款",
    generation_cancelled: "取消生成退款",
  } as Record<string, string>)[reason] || reason;
}
